/**
 * CoachPackagesPanel — lists coaching packages and handles create/enroll.
 * Uses the new schema-driven /api/packages API.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../config/environment';
import { markPackagePaymentPaid, createPackageInvoice } from '../services/paymentService';
import PackagePaymentModal from './PackagePaymentModal';

// ── Types ──────────────────────────────────────────────────────────────────────

interface PackageTypeConfig {
  package_type: string;
  display_name: string;
  description: string | null;
}

interface EventType {
  id: string;
  name: string;
  duration_minutes: number;
  description?: string | null;
  default_currency?: string | null;
}

interface CoachClientSlim {
  relationship_id: string;
  client_id: string;
  client_name: string;
  client_email: string;
}

interface SessionTypeOut {
  id: string;
  event_type_id: string;
  session_type_name: string;
  duration_minutes: number;
  allocation_method: string;
  session_count: number;
}

interface CoachingPackage {
  id: string;
  name: string;
  description: string | null;
  package_type: string;
  is_active: boolean;
  config_values: Record<string, unknown> | null;
  total_price_cents: number | null;
  currency: string | null;
  allow_installments: boolean | null;
  installment_count: number | null;
  installment_amount_cents: number | null;
  pay_in_full_price_cents: number | null;
  pay_in_full_discount_percent: number | null;
  duration_value: number | null;
  duration_unit: string | null;
  payment_method: string | null;
  autopay: boolean | null;
  allow_external_booking: boolean;
  session_types: SessionTypeOut[];
  created_at: string;
  enrollment_count: number;
}

interface EnrollModal {
  pkgId: string;
  pkgName: string;
  pkgType: string;               // fixed_duration | ongoing_monthly | session_bundle
  pkgDurationValue: number | null;
  pkgDurationUnit: string | null;
  pkgExpirationMonths: number | null;  // session_bundle expiry months
  pkgMonthlyPriceCents: number | null; // ongoing_monthly monthly charge
  pkgDiscountPercent: number | null;   // pay_in_full_discount_percent from package
  clientId: string;
  startDate: string;
  endDate: string;
  notes: string;
  paymentMethod: string;         // offline | stripe
  customPriceDollars: string;
  currency: string;
  allowInstallments: boolean;
  installmentFrequency: string;  // weekly | monthly
  installmentCount: string;      // auto-calculated but editable
  firstPaymentTiming: string;    // on_start_date | at_purchase | custom_date
  firstPaymentDate: string;      // ISO date when timing=custom_date
}

interface EnrollmentSessionUsage {
  session_type_name: string;
  duration_minutes: number;
  sessions_total: number;
  sessions_used: number;
}

interface PaymentRow {
  id: string;
  payment_type: string;
  installment_number: number | null;
  amount_cents: number;
  currency: string;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  invoice_token: string | null;
  invoice_id: string | null;
  failure_reason: string | null;
}

interface Enrollment {
  id: string;
  package_id: string;
  package_name: string;
  package_type: string | null;
  coach_client_id: number;
  client_name: string;
  sessions_used: number;
  status: string;
  start_date: string | null;
  end_date: string | null;
  enrolled_at: string;
  notes: string | null;
  session_usage: EnrollmentSessionUsage[];
  payment_method: string | null;
  autopay: boolean | null;
  effective_total_cents: number | null;
  effective_currency: string | null;
  effective_allow_installments: boolean | null;
  effective_installment_count: number | null;
  effective_installment_frequency: string | null;
  effective_first_payment_timing: string | null;
  payments: PaymentRow[];
  cancel_effective_month: string | null;
}

const CURRENCIES = ['USD','CAD','GBP','EUR','AUD','NZD','CHF','SGD','HKD','INR','ZAR','MXN'];

function currencyOptions(selected: string): string[] {
  const upper = selected.toUpperCase();
  return CURRENCIES.includes(upper) ? CURRENCIES : [upper, ...CURRENCIES];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function calcInstallmentCount(
  durationValue: number | null,
  durationUnit: string | null,
  frequency: string,
): string {
  if (!durationValue || !durationUnit) return '';
  const weeks = durationUnit === 'months' ? durationValue * 4 : durationValue;
  let count = 0;
  if (frequency === 'weekly')  count = weeks;
  if (frequency === 'monthly') count = durationUnit === 'months' ? durationValue : Math.ceil(weeks / 4);
  return count > 0 ? String(count) : '';
}

interface EditableScheduleRow {
  payment_number: number;
  due_date: string;         // YYYY-MM-DD, editable
  amount_dollars: string;   // dollar string, editable
  currency: string;
}

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

function apiBase() { return config.apiBaseUrl; }

function formatCurrency(cents: number | null, currency: string | null): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency ?? 'USD').toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function allocationLabel(method: string): string {
  return method === 'per_week' ? '/wk' : method === 'per_month' ? '/mo' : ' total';
}

function computeEndDate(startDate: string, durationValue: number | null, durationUnit: string | null): string {
  if (!startDate || !durationValue || !durationUnit) return '';
  const start = new Date(startDate + 'T00:00:00');
  if (durationUnit === 'months') {
    const totalMonths = start.getMonth() + durationValue;
    const y = start.getFullYear() + Math.floor(totalMonths / 12);
    const m = totalMonths % 12;
    const maxDay = new Date(y, m + 1, 0).getDate();
    return new Date(y, m, Math.min(start.getDate(), maxDay)).toISOString().split('T')[0];
  } else if (durationUnit === 'weeks') {
    return new Date(start.getTime() + durationValue * 7 * 86400000).toISOString().split('T')[0];
  }
  return '';
}

function formatDateDisplay(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    active:    'bg-green-900/40 text-green-400',
    completed: 'bg-blue-900/40 text-blue-400',
    cancelled: 'bg-gray-700 text-gray-400',
    paused:    'bg-yellow-900/40 text-yellow-400',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls[status] ?? 'bg-gray-700 text-gray-400'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ── Create-package form state ──────────────────────────────────────────────────

interface PkgForm {
  name: string;
  description: string;
  duration_value: string;
  duration_unit: string;
  total_price_dollars: string;
  currency: string;
  payment_method: string;
  pay_in_full_discount_percent: string;
  rollover_unused: boolean;       // ongoing_monthly only
  expiration_months: string;      // session_bundle only
}

const BLANK_PKG_FORM: PkgForm = {
  name: '',
  description: '',
  duration_value: '3',
  duration_unit: 'months',
  total_price_dollars: '',
  currency: 'USD',
  payment_method: 'offline',
  pay_in_full_discount_percent: '',
  rollover_unused: true,
  expiration_months: '',
};

interface OngoingForm {
  name: string;
  description: string;
  monthly_price_dollars: string;
  rollover_unused: boolean;
  currency: string;
  payment_method: string;
}

const BLANK_ONGOING_FORM: OngoingForm = {
  name: '', description: '',
  monthly_price_dollars: '',
  rollover_unused: true,
  currency: 'USD',
  payment_method: 'offline',
};

interface BundleForm {
  name: string;
  description: string;
  total_price_dollars: string;
  expiration_months: string;
  currency: string;
  payment_method: string;
  pay_in_full_discount_percent: string;
}

const BLANK_BUNDLE_FORM: BundleForm = {
  name: '', description: '',
  total_price_dollars: '',
  expiration_months: '',
  currency: 'USD',
  payment_method: 'offline',
  pay_in_full_discount_percent: '',
};

interface SessionTypeRow {
  event_type_id: string;
  allocation_method: string;
  session_count: string;
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  coachId: string;
  eventTypes: EventType[];
  clients: CoachClientSlim[];
  defaultCurrency: string;
  enforcePaymentPackages?: boolean;
  onGoToPaymentSettings?: () => void;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function CoachPackagesPanel({ coachId, eventTypes, clients, defaultCurrency, enforcePaymentPackages: initialEnforce, onGoToPaymentSettings }: Props) {

  const [packages, setPackages]         = useState<CoachingPackage[]>([]);
  const [enrollments, setEnrollments]   = useState<Enrollment[]>([]);
  const [packageTypes, setPackageTypes] = useState<PackageTypeConfig[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [stripeConnected, setStripeConnected] = useState<boolean>(false);
  const [enforcePaymentPackages, setEnforcePaymentPackages] = useState(initialEnforce ?? false);
  const [enforcePaymentSaving, setEnforcePaymentSaving] = useState(false);

  // All-enrollments tab
  const [panelTab, setPanelTab]                   = useState<'packages' | 'enrollments'>('packages');
  const [allEnrollments, setAllEnrollments]       = useState<Enrollment[]>([]);
  const [allEnrLoading, setAllEnrLoading]         = useState(false);
  const [allEnrError, setAllEnrError]             = useState<string | null>(null);
  const [enrStatusFilter, setEnrStatusFilter]     = useState<string>('active');
  const [enrPackageFilter, setEnrPackageFilter]   = useState<string>('');

  // Create form
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedPackageType, setSelectedPackageType] = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState<PkgForm>(BLANK_PKG_FORM);
  const [ongoingForm, setOngoingForm] = useState<OngoingForm>(BLANK_ONGOING_FORM);
  const [bundleForm, setBundleForm]   = useState<BundleForm>(BLANK_BUNDLE_FORM);
  const [sessionRows, setSessionRows] = useState<SessionTypeRow[]>([
    { event_type_id: '', allocation_method: 'per_month', session_count: '4' },
  ]);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError]   = useState<string | null>(null);

  // Edit form
  const [editingPkgId, setEditingPkgId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PkgForm>(BLANK_PKG_FORM);
  const [editSessionRows, setEditSessionRows] = useState<SessionTypeRow[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Enroll modal
  const [enrollModal, setEnrollModal] = useState<EnrollModal | null>(null);
  const [enrollSaving, setEnrollSaving] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [showEnrollConfirm, setShowEnrollConfirm] = useState(false);
  const [clientCardInfo, setClientCardInfo] = useState<{ has_card: boolean; card_last4?: string; card_brand?: string } | null>(null);

  // Enrollments modal (per package)
  const [enrollmentsModal, setEnrollmentsModal] = useState<{ pkgId: string; pkgName: string; pkgType: string } | null>(null);
  const [cancelEnrollTarget, setCancelEnrollTarget] = useState<{ id: string; client_name: string } | null>(null);
  const [cancelEffectiveMonth, setCancelEffectiveMonth] = useState<string>('');
  const [expandedEnrollments, setExpandedEnrollments] = useState<Set<string>>(new Set());
  const [enrollmentSessions, setEnrollmentSessions] = useState<Record<string, any[]>>({});
  const [enrollmentSessionsLoading, setEnrollmentSessionsLoading] = useState<Set<string>>(new Set());
  const [enrollmentPauses, setEnrollmentPauses] = useState<Record<string, { id: string; pause_month: string; rollover_sessions: boolean }[]>>({});

  // View/Edit enrollment modal
  const [viewEditEnrollment, setViewEditEnrollment] = useState<Enrollment | null>(null);
  const [viewEditNotes, setViewEditNotes] = useState('');
  const [viewEditStartDate, setViewEditStartDate] = useState('');
  const [viewEditEndDate, setViewEditEndDate] = useState('');
  const [viewEditPaymentMethod, setViewEditPaymentMethod] = useState('');
  const [viewEditSaving, setViewEditSaving] = useState(false);
  const [viewEditError, setViewEditError] = useState<string | null>(null);
  const [markPaidPrompt, setMarkPaidPrompt] = useState<{ enrollmentId: string; paymentId: string } | null>(null);
  const [markPaidMethod, setMarkPaidMethod] = useState('Cash');
  const [markPaidLoading, setMarkPaidLoading] = useState<string | null>(null); // payment_id being marked paid
  const [invoiceLoading, setInvoiceLoading] = useState<string | null>(null);   // payment_id having invoice created/resent
  const [chargeModal, setChargeModal] = useState<{ paymentId: string; enrollmentId: string; packageName: string; paymentLabel: string } | null>(null);

  // Payment schedule (fetched from backend, editable)
  const [editableSchedule, setEditableSchedule] = useState<EditableScheduleRow[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const scheduleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = authHeader();
      const [pkgRes, typesRes] = await Promise.all([
        fetch(`${apiBase()}/packages?is_active=true`, { headers }),
        fetch(`${apiBase()}/packages/types`, { headers }),
      ]);
      if (pkgRes.ok) setPackages(await pkgRes.json());
      if (typesRes.ok) setPackageTypes(await typesRes.json());
    } catch {
      setError('Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, []);

  async function fetchAllEnrollments(statusFilter?: string, packageFilter?: string) {
    setAllEnrLoading(true);
    setAllEnrError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (packageFilter) params.set('package_id', packageFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${apiBase()}/packages/enrollments${qs}`, { headers: authHeader() });
      if (!res.ok) throw new Error('Failed to load enrollments');
      setAllEnrollments(await res.json());
    } catch (e) {
      setAllEnrError(e instanceof Error ? e.message : 'Failed to load enrollments');
    } finally {
      setAllEnrLoading(false);
    }
  }

  async function loadEnrollmentsForPackage(pkgId: string): Promise<Enrollment[]> {
    const headers = authHeader();
    const res = await fetch(`${apiBase()}/packages/enrollments?package_id=${pkgId}`, { headers });
    if (!res.ok) return [];
    const data: Enrollment[] = await res.json();
    setEnrollments(prev => [...prev.filter(e => e.package_id !== pkgId), ...data]);
    return data;
  }

  useEffect(() => {
    fetchAll();
    fetch(`${apiBase()}/coaches/me/onboarding-status`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setStripeConnected(!!d?.setup_payments))
      .catch(() => setStripeConnected(false));
  }, [fetchAll]);

  // ── Schedule preview (debounced, fetched from backend) ──────────────────────

  useEffect(() => {
    if (!enrollModal) { setEditableSchedule([]); return; }
    const m = enrollModal;
    // ongoing_monthly has no upfront schedule — billing is generated monthly by cron
    if (m.pkgType === 'ongoing_monthly') { setEditableSchedule([]); return; }
    // session_bundle uses a manual schedule builder — backend preview is not used
    if (m.pkgType === 'session_bundle') { return; }
    if (!m.startDate || !m.customPriceDollars) { setEditableSchedule([]); return; }
    const totalCents = Math.round(parseFloat(m.customPriceDollars) * 100);
    if (totalCents < 1) { setEditableSchedule([]); return; }
    const effectiveCents = (!m.allowInstallments && m.pkgDiscountPercent && m.pkgDiscountPercent > 0)
      ? Math.round(totalCents * (1 - m.pkgDiscountPercent / 100))
      : totalCents;

    if (scheduleTimerRef.current) clearTimeout(scheduleTimerRef.current);
    scheduleTimerRef.current = setTimeout(async () => {
      setScheduleLoading(true);
      try {
        const body: Record<string, unknown> = {
          start_date: m.startDate,
          total_price_cents: effectiveCents,
          currency: m.currency,
          allow_installments: m.allowInstallments,
          first_payment_timing: m.firstPaymentTiming,
        };
        if (m.allowInstallments) {
          body.installment_count = parseInt(m.installmentCount || '1');
          body.installment_frequency = m.installmentFrequency;
        }
        if (m.firstPaymentTiming === 'custom_date' && m.firstPaymentDate) {
          body.first_payment_date = m.firstPaymentDate;
        }
        const res = await fetch(`${apiBase()}/packages/${m.pkgId}/preview-schedule`, {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data = await res.json();
          const today = new Date().toISOString().split('T')[0];
          setEditableSchedule(data.schedule.map((r: { payment_number: number; due_date: string; amount_cents: number; currency: string }) => ({
            payment_number: r.payment_number,
            due_date: r.due_date < today ? today : r.due_date,
            amount_dollars: (r.amount_cents / 100).toFixed(2),
            currency: r.currency,
          })));
        }
      } finally {
        setScheduleLoading(false);
      }
    }, 350);
    return () => { if (scheduleTimerRef.current) clearTimeout(scheduleTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrollModal?.pkgId,
    enrollModal?.pkgType,
    enrollModal?.startDate,
    enrollModal?.customPriceDollars,
    enrollModal?.currency,
    enrollModal?.allowInstallments,
    enrollModal?.installmentCount,
    enrollModal?.installmentFrequency,
    enrollModal?.firstPaymentTiming,
    enrollModal?.firstPaymentDate,
  ]);

  // Fetch client card status when Stripe is selected in enroll modal
  useEffect(() => {
    if (!enrollModal || enrollModal.paymentMethod !== 'stripe' || !enrollModal.clientId) {
      setClientCardInfo(null);
      return;
    }
    const selectedClient = clients.find(c => c.relationship_id === enrollModal.clientId);
    if (!selectedClient?.client_id) { setClientCardInfo(null); return; }
    fetch(`${apiBase()}/payments/client/${selectedClient.client_id}/payment-method`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setClientCardInfo(d))
      .catch(() => setClientCardInfo(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollModal?.clientId, enrollModal?.paymentMethod]);

  // ── Create package ───────────────────────────────────────────────────────────

  function openCreate() {
    setSelectedPackageType(packageTypes[0]?.package_type ?? 'fixed_duration');
    setShowTypeSelector(true);
    setShowForm(false);
  }

  function confirmTypeSelection() {
    setShowTypeSelector(false);
    const currency = defaultCurrency
      || eventTypes.find(et => et.default_currency)?.default_currency
      || 'USD';
    setForm({ ...BLANK_PKG_FORM, currency });
    setOngoingForm({ ...BLANK_ONGOING_FORM, currency });
    setBundleForm({ ...BLANK_BUNDLE_FORM, currency });
    const defaultEventTypeId = eventTypes[0]?.id ?? '';
    if (selectedPackageType === 'ongoing_monthly') {
      setSessionRows([{ event_type_id: defaultEventTypeId, allocation_method: 'per_month', session_count: '4' }]);
    } else if (selectedPackageType === 'session_bundle') {
      setSessionRows([{ event_type_id: defaultEventTypeId, allocation_method: 'total', session_count: '10' }]);
    } else {
      // fixed_duration: default unit is 'months', so default to per_month
      setSessionRows([{ event_type_id: defaultEventTypeId, allocation_method: 'per_month', session_count: '4' }]);
    }
    setFormError(null);
    setShowForm(true);
  }

  async function savePackage() {
    setFormSaving(true);
    setFormError(null);

    let configValues: Record<string, unknown>;
    let name: string;
    let description: string | null;

    if (selectedPackageType === 'ongoing_monthly') {
      const monthlyCents = Math.round(parseFloat(ongoingForm.monthly_price_dollars || '0') * 100);
      if (!ongoingForm.name.trim() || monthlyCents < 1) {
        setFormError('Name and monthly price are required.');
        setFormSaving(false);
        return;
      }
      name = ongoingForm.name.trim();
      description = ongoingForm.description.trim() || null;
      const sessionsPerMonth = sessionRows.reduce((sum, r) => sum + (parseInt(r.session_count) || 0), 0);
      configValues = {
        monthly_price_cents: monthlyCents,
        sessions_per_month:  sessionsPerMonth || undefined,
        rollover_unused:     ongoingForm.rollover_unused,
        currency:            ongoingForm.currency,
        payment_method:      ongoingForm.payment_method,
      };
    } else if (selectedPackageType === 'session_bundle') {
      const totalCents = Math.round(parseFloat(bundleForm.total_price_dollars || '0') * 100);
      if (!bundleForm.name.trim() || totalCents < 1) {
        setFormError('Name and bundle price are required.');
        setFormSaving(false);
        return;
      }
      name = bundleForm.name.trim();
      description = bundleForm.description.trim() || null;
      const totalSessions = sessionRows.reduce((sum, r) => sum + (parseInt(r.session_count) || 0), 0);
      configValues = {
        total_sessions:     totalSessions,
        total_price_cents:  totalCents,
        currency:           bundleForm.currency,
        payment_method:     bundleForm.payment_method,
        allow_installments: false,
        ...(bundleForm.expiration_months !== '' ? { expiration_months: parseInt(bundleForm.expiration_months) } : {}),
        ...(bundleForm.pay_in_full_discount_percent !== '' ? { pay_in_full_discount_percent: parseFloat(bundleForm.pay_in_full_discount_percent) } : {}),
      };
    } else {
      // fixed_duration (default)
      const totalCents = Math.round(parseFloat(form.total_price_dollars || '0') * 100);
      if (!form.name.trim() || totalCents < 1) {
        setFormError('Name and total price are required.');
        setFormSaving(false);
        return;
      }
      name = form.name.trim();
      description = form.description.trim() || null;
      configValues = {
        duration_value:     parseInt(form.duration_value),
        duration_unit:      form.duration_unit,
        total_price_cents:  totalCents,
        currency:           form.currency,
        payment_method:     'offline',
        allow_installments: false,
        ...(form.pay_in_full_discount_percent !== ''
          ? { pay_in_full_discount_percent: parseFloat(form.pay_in_full_discount_percent) }
          : {}),
      };
    }

    const validRows = sessionRows.filter(r => r.event_type_id && parseInt(r.session_count) >= 1);
    if (!validRows.length) {
      setFormError('Add at least one session type.');
      setFormSaving(false);
      return;
    }

    const body = {
      package_type: selectedPackageType || 'fixed_duration',
      name,
      description,
      session_types: validRows.map((r, i) => ({
        event_type_id:     r.event_type_id,
        allocation_method: r.allocation_method,
        session_count:     parseInt(r.session_count),
        display_order:     i,
      })),
      config_values: configValues,
    };

    try {
      const res = await fetch(`${apiBase()}/packages`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        if (typeof detail === 'string') {
          setFormError(detail);
        } else if (detail?.config_errors) {
          setFormError(Object.values(detail.config_errors as Record<string, string>).join('; ') || 'Validation failed');
        } else {
          setFormError('Save failed');
        }
        return;
      }
      setShowForm(false);
      await fetchAll();
    } finally {
      setFormSaving(false);
    }
  }

  async function toggleEnforcePaymentPackages() {
    const newValue = !enforcePaymentPackages;
    setEnforcePaymentSaving(true);
    try {
      const res = await fetch(`${apiBase()}/coaches/${coachId}`, {
        method: 'PATCH',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enforce_payment_packages: newValue }),
      });
      if (res.ok) setEnforcePaymentPackages(newValue);
    } finally {
      setEnforcePaymentSaving(false);
    }
  }

  async function toggleExternalBooking(pkg: CoachingPackage) {
    const newValue = !pkg.allow_external_booking;
    const res = await fetch(`${apiBase()}/packages/${pkg.id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ allow_external_booking: newValue }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.detail || 'Could not update package');
      return;
    }
    setPackages(prev => prev.map(p => p.id === pkg.id ? { ...p, allow_external_booking: newValue } : p));
  }

  async function deletePackage(pkg: CoachingPackage) {
    if (!window.confirm(`Delete "${pkg.name}"? This cannot be undone.`)) return;
    const res = await fetch(`${apiBase()}/packages/${pkg.id}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.detail || 'Could not delete package');
      return;
    }
    await fetchAll();
  }

  function openEdit(pkg: CoachingPackage) {
    setEditingPkgId(pkg.id);
    const cv = (pkg.config_values ?? {}) as Record<string, unknown>;
    const monthlyPriceCents = typeof cv.monthly_price_cents === 'number' ? cv.monthly_price_cents : null;
    const priceForForm = pkg.package_type === 'ongoing_monthly' && monthlyPriceCents != null
      ? String(monthlyPriceCents / 100)
      : pkg.total_price_cents != null ? String(pkg.total_price_cents / 100) : '';
    setEditForm({
      name: pkg.name,
      description: pkg.description ?? '',
      duration_value: String(pkg.duration_value ?? 3),
      duration_unit: pkg.duration_unit ?? 'months',
      total_price_dollars: priceForForm,
      currency: pkg.currency ?? 'USD',
      payment_method: typeof cv.payment_method === 'string' ? cv.payment_method : (pkg.payment_method ?? 'offline'),
      pay_in_full_discount_percent: cv.pay_in_full_discount_percent != null ? String(cv.pay_in_full_discount_percent) : '',
      rollover_unused: typeof cv.rollover_unused === 'boolean' ? cv.rollover_unused : true,
      expiration_months: cv.expiration_months != null ? String(cv.expiration_months) : '',
    });
    setEditSessionRows(
      (pkg.session_types ?? []).map(st => ({
        event_type_id: st.event_type_id,
        allocation_method: st.allocation_method,
        session_count: String(st.session_count),
      }))
    );
    setEditError(null);
  }

  async function saveEdit() {
    if (!editingPkgId) return;
    const pkgBeingEdited = packages.find(p => p.id === editingPkgId);
    const editingPkgType = pkgBeingEdited?.package_type ?? 'fixed_duration';
    const priceCents = Math.round(parseFloat(editForm.total_price_dollars || '0') * 100);
    if (!editForm.name.trim() || priceCents < 1) {
      setEditError('Name and price are required.');
      return;
    }
    if (editSessionRows.length === 0 || editSessionRows.some(r => !r.event_type_id)) {
      setEditError('All session types must be selected.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      let configValues: Record<string, unknown>;
      if (editingPkgType === 'ongoing_monthly') {
        configValues = {
          monthly_price_cents: priceCents,
          currency: editForm.currency,
          payment_method: editForm.payment_method,
          rollover_unused: editForm.rollover_unused,
        };
      } else if (editingPkgType === 'session_bundle') {
        const totalSessions = editSessionRows.reduce((s, r) => s + (parseInt(r.session_count) || 0), 0);
        configValues = {
          total_sessions: totalSessions,
          total_price_cents: priceCents,
          currency: editForm.currency,
          payment_method: editForm.payment_method,
          allow_installments: false,
          ...(editForm.expiration_months !== '' ? { expiration_months: parseInt(editForm.expiration_months) } : {}),
          ...(editForm.pay_in_full_discount_percent !== '' ? { pay_in_full_discount_percent: parseFloat(editForm.pay_in_full_discount_percent) } : {}),
        };
      } else {
        // fixed_duration
        configValues = {
          duration_value: parseInt(editForm.duration_value),
          duration_unit: editForm.duration_unit,
          total_price_cents: priceCents,
          currency: editForm.currency,
          payment_method: editForm.payment_method,
          allow_installments: false,
          ...(editForm.pay_in_full_discount_percent !== '' ? { pay_in_full_discount_percent: parseFloat(editForm.pay_in_full_discount_percent) } : {}),
        };
      }
      const sessionTypesPayload = editSessionRows.map(r => ({
        event_type_id: r.event_type_id,
        allocation_method: r.allocation_method,
        session_count: parseInt(r.session_count) || 1,
      }));
      const res = await fetch(`${apiBase()}/packages/${editingPkgId}`, {
        method: 'PATCH',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          config_values: configValues,
          session_types: sessionTypesPayload,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setEditError(typeof data.detail === 'string' ? data.detail : 'Save failed');
        return;
      }
      setEditingPkgId(null);
      await fetchAll();
    } finally {
      setEditSaving(false);
    }
  }

  // ── Enroll ────────────────────────────────────────────────────────────────────

  function openEnroll(pkg: CoachingPackage) {
    setEnrollError(null);
    setEditableSchedule([]);
    setScheduleOpen(true);
    const freq = 'monthly';
    const config = (pkg.config_values ?? {}) as Record<string, unknown>;
    const expirationMonths = typeof config.expiration_months === 'number' ? config.expiration_months : null;
    const monthlyPriceCents = typeof config.monthly_price_cents === 'number' ? config.monthly_price_cents : null;
    const discountPercent = typeof config.pay_in_full_discount_percent === 'number' ? config.pay_in_full_discount_percent : null;
    setEnrollModal({
      pkgId:                 pkg.id,
      pkgName:               pkg.name,
      pkgType:               pkg.package_type,
      pkgDurationValue:      pkg.duration_value,
      pkgDurationUnit:       pkg.duration_unit,
      pkgExpirationMonths:   expirationMonths,
      pkgMonthlyPriceCents:  monthlyPriceCents,
      pkgDiscountPercent:    discountPercent,
      clientId:              '',
      startDate:             '',
      endDate:               '',
      notes:                 '',
      paymentMethod:         pkg.payment_method ?? 'offline',
      customPriceDollars:    pkg.package_type === 'ongoing_monthly' && monthlyPriceCents != null
        ? (monthlyPriceCents / 100).toFixed(2)
        : pkg.total_price_cents != null
          ? (pkg.total_price_cents / 100).toFixed(2)
          : '',
      currency:              pkg.currency ?? 'USD',
      allowInstallments:     pkg.allow_installments ?? false,
      installmentFrequency:  freq,
      installmentCount:      pkg.installment_count != null
        ? String(pkg.installment_count)
        : calcInstallmentCount(pkg.duration_value, pkg.duration_unit, freq),
      firstPaymentTiming:    'on_start_date',
      firstPaymentDate:      '',
    });
  }

  function updateScheduleRow(index: number, field: 'due_date' | 'amount_dollars', value: string) {
    setEditableSchedule(rows => rows.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }

  function updateEnroll<K extends keyof EnrollModal>(key: K, val: EnrollModal[K]) {
    setEnrollModal(m => {
      if (!m) return m;
      const next = { ...m, [key]: val };
      // When frequency changes and count hasn't been manually overridden, recalculate
      if (key === 'installmentFrequency') {
        next.installmentCount = calcInstallmentCount(
          m.pkgDurationValue, m.pkgDurationUnit, val as string,
        );
      }
      return next;
    });
  }

  async function submitEnroll() {
    if (!enrollModal || !enrollModal.clientId || !enrollModal.startDate) return;
    setShowEnrollConfirm(false);

    const today = new Date().toISOString().split('T')[0];
    if (enrollModal.firstPaymentTiming === 'custom_date' && enrollModal.firstPaymentDate && enrollModal.firstPaymentDate < today) {
      setEnrollError('First payment date cannot be in the past.');
      return;
    }
    const pastDates = editableSchedule.filter(r => r.due_date && r.due_date < today);
    if (pastDates.length > 0) {
      setEnrollError(`Payment date${pastDates.length > 1 ? 's' : ''} cannot be in the past.`);
      return;
    }

    setEnrollSaving(true);
    setEnrollError(null);
    try {
      let priceCents: number | undefined = undefined;
      if (enrollModal.customPriceDollars !== '') {
        const raw = parseFloat(enrollModal.customPriceDollars);
        const discountPct = enrollModal.pkgDiscountPercent ?? 0;
        const effective = (!enrollModal.allowInstallments && discountPct > 0)
          ? raw * (1 - discountPct / 100)
          : raw;
        priceCents = Math.round(effective * 100);
      }
      const instCount = enrollModal.allowInstallments && enrollModal.installmentCount !== ''
        ? parseInt(enrollModal.installmentCount)
        : undefined;

      const body: Record<string, unknown> = {
        coach_client_id:             parseInt(enrollModal.clientId),
        start_date:                  enrollModal.startDate,
        end_date:                    enrollModal.endDate || null,
        notes:                       enrollModal.notes || null,
        custom_payment_method:       enrollModal.paymentMethod || null,
        custom_allow_installments:   enrollModal.allowInstallments,
        custom_installment_frequency: enrollModal.allowInstallments ? enrollModal.installmentFrequency || null : null,
        custom_first_payment_timing:  enrollModal.firstPaymentTiming || null,
        custom_first_payment_date:   enrollModal.firstPaymentTiming === 'custom_date' && enrollModal.firstPaymentDate
          ? enrollModal.firstPaymentDate
          : null,
      };
      if (priceCents !== undefined) body.custom_total_price_cents = priceCents;
      if (instCount  !== undefined) body.custom_installment_count  = instCount;
      // Send edited schedule — backend validates total
      if (editableSchedule.length > 0) {
        body.payment_schedule = editableSchedule.map(r => ({
          due_date: r.due_date,
          amount_cents: Math.round(parseFloat(r.amount_dollars || '0') * 100),
        }));
      }

      const res = await fetch(`${apiBase()}/packages/${enrollModal.pkgId}/enroll`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setEnrollError(typeof data.detail === 'string' ? data.detail : 'Enrollment failed');
        return;
      }
      setEnrollModal(null);
      await fetchAll();
    } finally {
      setEnrollSaving(false);
    }
  }

  async function toggleEnrollmentSessions(enrollmentId: string) {
    const next = new Set(expandedEnrollments);
    if (next.has(enrollmentId)) {
      next.delete(enrollmentId);
      setExpandedEnrollments(next);
      return;
    }
    next.add(enrollmentId);
    setExpandedEnrollments(next);
    if (enrollmentSessions[enrollmentId]) return; // already loaded
    setEnrollmentSessionsLoading(prev => new Set(prev).add(enrollmentId));
    try {
      const res = await fetch(`${apiBase()}/packages/enrollments/${enrollmentId}/sessions`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setEnrollmentSessions(prev => ({ ...prev, [enrollmentId]: data }));
      }
    } finally {
      setEnrollmentSessionsLoading(prev => { const s = new Set(prev); s.delete(enrollmentId); return s; });
    }
  }

  async function saveViewEdit() {
    if (!viewEditEnrollment) return;
    setViewEditSaving(true);
    setViewEditError(null);
    try {
      const isFixed = viewEditEnrollment.package_type === 'fixed_duration';
      const body: Record<string, unknown> = isFixed ? {} : { notes: viewEditNotes };
      if (!isFixed && viewEditStartDate && viewEditStartDate !== (viewEditEnrollment.start_date ?? '')) {
        body.start_date = viewEditStartDate;
      }
      if (viewEditEndDate && viewEditEndDate !== (viewEditEnrollment.end_date ?? '')) {
        body.end_date = viewEditEndDate;
      }
      if (viewEditPaymentMethod && viewEditPaymentMethod !== (viewEditEnrollment.payment_method ?? 'offline')) {
        body.payment_method = viewEditPaymentMethod;
      }
      const res = await fetch(`${apiBase()}/packages/enrollments/${viewEditEnrollment.id}`, {
        method: 'PATCH',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setViewEditError(typeof data.detail === 'string' ? data.detail : 'Save failed');
        return;
      }
      await fetchAll();
      setViewEditEnrollment(e => e
        ? { ...e, notes: viewEditNotes, start_date: viewEditStartDate || e.start_date, end_date: viewEditEndDate || e.end_date }
        : null
      );
    } finally {
      setViewEditSaving(false);
    }
  }

  function handleMarkPaid(enrollmentId: string, paymentId: string) {
    setMarkPaidMethod('Cash');
    setMarkPaidPrompt({ enrollmentId, paymentId });
  }

  async function confirmMarkPaid() {
    if (!markPaidPrompt) return;
    const { enrollmentId, paymentId } = markPaidPrompt;
    setMarkPaidPrompt(null);
    setMarkPaidLoading(paymentId);
    setViewEditError(null);
    try {
      await markPackagePaymentPaid(enrollmentId, paymentId, markPaidMethod);
      await fetchAll();
      if (viewEditEnrollment?.id === enrollmentId) {
        setViewEditEnrollment(e => e ? {
          ...e,
          payments: (e.payments ?? []).map(p =>
            p.id === paymentId ? { ...p, status: 'manual', payment_method: markPaidMethod } : p
          ),
        } : null);
      }
    } catch (err) {
      setViewEditError(err instanceof Error ? err.message : 'Failed to mark payment as paid');
    } finally {
      setMarkPaidLoading(null);
    }
  }

  async function handleCreateInvoice(enrollmentId: string, paymentId: string, hasInvoice: boolean) {
    const verb = hasInvoice ? 'Resend the invoice email?' : 'Create and send invoice to client?';
    if (!window.confirm(verb)) return;
    setInvoiceLoading(paymentId);
    setViewEditError(null);
    try {
      const result = await createPackageInvoice(enrollmentId, paymentId);
      if (viewEditEnrollment?.id === enrollmentId) {
        setViewEditEnrollment(e => e ? {
          ...e,
          payments: (e.payments ?? []).map(p =>
            p.id === paymentId
              ? { ...p, invoice_token: result.invoice_token, invoice_id: result.invoice_id }
              : p
          ),
        } : null);
      }
    } catch (err) {
      setViewEditError(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setInvoiceLoading(null);
    }
  }

  function handleChargeStripe(enrollmentId: string, paymentId: string, packageName: string, paymentLabel: string) {
    setChargeModal({ paymentId, enrollmentId, packageName, paymentLabel });
  }

  async function handleChargeSuccess(enrollmentId: string, paymentId: string) {
    await fetchAll();
    if (viewEditEnrollment?.id === enrollmentId) {
      setViewEditEnrollment(e => e ? {
        ...e,
        payments: (e.payments ?? []).map(p =>
          p.id === paymentId ? { ...p, status: 'succeeded' } : p
        ),
      } : null);
    }
  }

  async function updateEnrollmentStatus(id: string, status: string) {
    const res = await fetch(`${apiBase()}/packages/enrollments/${id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.detail || 'Update failed');
      return;
    }
    await fetchAll();
  }

  async function confirmCancelEnrollment() {
    if (!cancelEnrollTarget || !cancelEffectiveMonth) return;
    const res = await fetch(`${apiBase()}/packages/enrollments/${cancelEnrollTarget.id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ cancel_effective_month: cancelEffectiveMonth }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.detail || 'Update failed');
      return;
    }
    setCancelEnrollTarget(null);
    setCancelEffectiveMonth('');
    await fetchAll();
  }

  async function loadPausesForPackage(pkgId: string, freshEnrollments?: Enrollment[]) {
    const pkgEnrollments = (freshEnrollments ?? enrollments).filter(e => e.package_id === pkgId);
    const headers = authHeader();
    const results: Record<string, any[]> = {};
    await Promise.all(pkgEnrollments.map(async (e) => {
      const res = await fetch(`${apiBase()}/packages/enrollments/${e.id}/pauses`, { headers });
      if (res.ok) results[e.id] = await res.json();
    }));
    setEnrollmentPauses(prev => ({ ...prev, ...results }));
  }

  async function pauseEnrollment(enrollmentId: string) {
    const now = new Date();
    const pauseMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const res = await fetch(`${apiBase()}/packages/enrollments/${enrollmentId}/pauses`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ pause_month: pauseMonth, rollover_sessions: true }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert((data as any).detail || 'Failed to pause');
      return;
    }
    if (enrollmentsModal) await loadPausesForPackage(enrollmentsModal.pkgId);
  }

  async function unpauseEnrollment(enrollmentId: string, pauseMonth: string) {
    const res = await fetch(`${apiBase()}/packages/enrollments/${enrollmentId}/pauses/${pauseMonth}`, {
      method: 'DELETE',
      headers: authHeader(),
    });
    if (!res.ok) { alert('Failed to remove pause'); return; }
    if (enrollmentsModal) await loadPausesForPackage(enrollmentsModal.pkgId);
  }

  function futureMonthOptions(): { value: string; label: string }[] {
    const options = [];
    const now = new Date();
    for (let i = 1; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) return <p className="text-sm text-gray-400">Loading packages…</p>;
  if (error)   return <p className="text-sm text-red-400">{error}</p>;

  // ── Enrollment summary helpers ──────────────────────────────────────────────
  function enrSessionSummary(e: Enrollment): { used: number; total: number } {
    if (!e.session_usage?.length) return { used: e.sessions_used ?? 0, total: 0 };
    return {
      used: e.session_usage.reduce((s: number, u: EnrollmentSessionUsage) => s + (u.sessions_used ?? 0), 0),
      total: e.session_usage.reduce((s: number, u: EnrollmentSessionUsage) => s + (u.sessions_total ?? 0), 0),
    };
  }

  function enrPaymentSummary(e: Enrollment): { collected: number; total: number; hasOutstanding: boolean; hasFailed: boolean; nextDue: string | null; currency: string } {
    const cur = (e.effective_currency ?? 'USD').toUpperCase();
    const total = e.effective_total_cents ?? 0;
    let collected = 0;
    let outstanding = 0;
    let hasFailed = false;
    let nextDue: string | null = null;
    for (const p of e.payments ?? []) {
      if (p.status === 'manual' || p.status === 'paid' || p.status === 'succeeded') collected += p.amount_cents ?? 0;
      if (p.status === 'pending') {
        outstanding += p.amount_cents ?? 0;
        if (p.due_date && (!nextDue || p.due_date < nextDue)) nextDue = p.due_date;
      }
      if (p.status === 'failed') hasFailed = true;
    }
    return { collected, total, hasOutstanding: outstanding > 0, hasFailed, nextDue, currency: cur };
  }

  function openViewEdit(e: Enrollment) {
    setViewEditEnrollment(e);
    setViewEditNotes(e.notes ?? '');
    setViewEditStartDate(e.start_date ?? '');
    setViewEditEndDate(e.end_date ?? '');
    setViewEditPaymentMethod(e.payment_method ?? 'offline');
    setViewEditError(null);
  }

  return (
    <div className="space-y-6">

      {/* ── Tab bar ───────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-dark-border">
        {(['packages', 'enrollments'] as const).map(t => (
          <button
            key={t}
            onClick={() => {
              setPanelTab(t);
              if (t === 'enrollments' && allEnrollments.length === 0 && !allEnrLoading) {
                fetchAllEnrollments(enrStatusFilter, enrPackageFilter);
              }
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              panelTab === t
                ? 'border-primary-500 text-white'
                : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {t === 'packages' ? 'Packages' : 'Enrollments'}
          </button>
        ))}
      </div>

      {/* ── All Enrollments tab ───────────────────────────────────────────────── */}
      {panelTab === 'enrollments' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={enrStatusFilter}
              onChange={e => {
                setEnrStatusFilter(e.target.value);
                fetchAllEnrollments(e.target.value, enrPackageFilter);
              }}
              className="bg-dark-canvas border border-dark-border text-sm text-white rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-500"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="paused">Paused</option>
            </select>
            <select
              value={enrPackageFilter}
              onChange={e => {
                setEnrPackageFilter(e.target.value);
                fetchAllEnrollments(enrStatusFilter, e.target.value);
              }}
              className="bg-dark-canvas border border-dark-border text-sm text-white rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-500"
            >
              <option value="">All packages</option>
              {packages.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={() => fetchAllEnrollments(enrStatusFilter, enrPackageFilter)}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              ↻ Refresh
            </button>
            <span className="ml-auto text-xs text-gray-500">
              {allEnrLoading ? 'Loading…' : `${allEnrollments.length} enrollment${allEnrollments.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          {allEnrError && <p className="text-sm text-red-400">{allEnrError}</p>}

          {!allEnrLoading && allEnrollments.length === 0 && !allEnrError && (
            <p className="text-sm text-gray-500 italic py-4 text-center">No enrollments found.</p>
          )}

          {/* Table */}
          {allEnrollments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-dark-border text-left">
                    <th className="pb-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Client</th>
                    <th className="pb-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Package</th>
                    <th className="pb-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="pb-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Sessions</th>
                    <th className="pb-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Payment</th>
                    <th className="pb-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Next Due</th>
                    <th className="pb-2 text-xs font-medium text-gray-500 uppercase tracking-wide"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {allEnrollments.map(e => {
                    const sess = enrSessionSummary(e);
                    const pay = enrPaymentSummary(e);
                    const sessPercent = sess.total > 0 ? Math.min(100, (sess.used / sess.total) * 100) : 0;
                    return (
                      <tr key={e.id} className="hover:bg-dark-canvas/40 transition-colors">
                        <td className="py-3 pr-4">
                          <span className="text-white font-medium">{e.client_name}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-gray-300">{e.package_name}</span>
                        </td>
                        <td className="py-3 pr-4">
                          <StatusBadge status={e.status} />
                        </td>
                        <td className="py-3 pr-4">
                          {sess.total > 0 ? (
                            <div className="flex items-center gap-2 min-w-[80px]">
                              <div className="flex-1 bg-dark-border rounded-full h-1.5 min-w-[48px]">
                                <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: `${sessPercent}%` }} />
                              </div>
                              <span className="text-xs text-gray-400 whitespace-nowrap">{sess.used}/{sess.total}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-600">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <div className="space-y-0.5">
                            <span className="text-xs text-gray-300 whitespace-nowrap">
                              {formatCurrency(pay.collected, pay.currency)} / {formatCurrency(pay.total, pay.currency)}
                            </span>
                            {pay.hasFailed && (
                              <span className="block text-[10px] font-medium text-red-400">Payment failed</span>
                            )}
                            {!pay.hasFailed && pay.hasOutstanding && (
                              <span className="block text-[10px] text-yellow-400">Outstanding</span>
                            )}
                            {!pay.hasFailed && !pay.hasOutstanding && pay.total > 0 && (
                              <span className="block text-[10px] text-green-400">Paid in full</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span className="text-xs text-gray-400">
                            {pay.nextDue ? formatDateDisplay(pay.nextDue) : '—'}
                          </span>
                        </td>
                        <td className="py-3">
                          <button
                            onClick={() => openViewEdit(e)}
                            className="text-xs text-gray-300 hover:text-white border border-dark-border px-2.5 py-1 rounded-lg transition-colors whitespace-nowrap"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Packages tab ─────────────────────────────────────────────────────── */}
      {panelTab === 'packages' && <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">{packages.length} package{packages.length !== 1 ? 's' : ''}</p>
            <button
              onClick={openCreate}
              className="text-sm px-3 py-1.5 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors"
            >
              + New Package
            </button>
          </div>

          {/* Enforce payment at enrollment */}
          <div className="flex items-center justify-between bg-dark-canvas border border-dark-border rounded-xl px-4 py-3">
            <div>
              <p className="text-xs font-medium text-gray-300">Require payment to enroll</p>
              <p className="text-xs text-gray-500 mt-0.5">Clients must pay the full package price upfront when enrolling online.</p>
            </div>
            <button
              onClick={toggleEnforcePaymentPackages}
              disabled={enforcePaymentSaving}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${enforcePaymentPackages ? 'bg-primary-600' : 'bg-gray-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${enforcePaymentPackages ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Type selector */}
          {showTypeSelector && (
            <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-white">Choose a package type</h3>
                <p className="text-xs text-gray-500 mt-0.5">Select the type of coaching package you want to create.</p>
              </div>
              <div className="space-y-2">
                {packageTypes.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No package types available.</p>
                ) : packageTypes.map(pt => (
                  <button
                    key={pt.package_type}
                    type="button"
                    onClick={() => setSelectedPackageType(pt.package_type)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                      selectedPackageType === pt.package_type
                        ? 'border-primary-600 bg-primary-900/30'
                        : 'border-dark-border hover:border-gray-600 bg-dark-canvas'
                    }`}
                  >
                    <p className={`text-sm font-medium ${selectedPackageType === pt.package_type ? 'text-primary-300' : 'text-gray-200'}`}>
                      {pt.display_name}
                    </p>
                    {pt.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{pt.description}</p>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={confirmTypeSelection}
                  disabled={!selectedPackageType}
                  className="px-4 py-1.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Continue
                </button>
                <button
                  onClick={() => setShowTypeSelector(false)}
                  className="px-4 py-1.5 border border-dark-border text-gray-300 hover:text-white text-sm rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Create form */}
          {showForm && (
            <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-5">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-white">
                  {packageTypes.find(t => t.package_type === selectedPackageType)?.display_name ?? 'New Package'}
                </h3>
                <span className="text-xs text-gray-500 bg-dark-canvas border border-dark-border rounded-full px-2 py-0.5">
                  {selectedPackageType}
                </span>
              </div>

              {/* ── ongoing_monthly form ── */}
              {selectedPackageType === 'ongoing_monthly' && (<>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Package Name *</label>
                    <input className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="e.g. Monthly EF Coaching" value={ongoingForm.name} onChange={e => setOngoingForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Description</label>
                    <textarea rows={2} className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600 resize-none" placeholder="What's included…" value={ongoingForm.description} onChange={e => setOngoingForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Monthly Pricing</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Monthly Price ({ongoingForm.currency}) *</label>
                      <input type="number" min="0" step="0.01" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="500.00" value={ongoingForm.monthly_price_dollars} onChange={e => setOngoingForm(f => ({ ...f, monthly_price_dollars: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Currency</label>
                      <select className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={ongoingForm.currency} onChange={e => setOngoingForm(f => ({ ...f, currency: e.target.value }))}>
                        {currencyOptions(ongoingForm.currency).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="accent-primary-500" checked={ongoingForm.rollover_unused} onChange={e => setOngoingForm(f => ({ ...f, rollover_unused: e.target.checked }))} />
                    <span className="text-xs text-gray-300">Roll over unused sessions month to month</span>
                  </label>
                </div>
                {/* Session type selector for ongoing (locked to per_month) */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Session Types</p>
                  <div className="space-y-2">
                    {sessionRows.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-7">
                          <select className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={row.event_type_id} onChange={e => setSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, event_type_id: e.target.value } : r))}>
                            <option value="">Select session type…</option>
                            {eventTypes.map(et => <option key={et.id} value={et.id}>{et.name} ({et.duration_minutes} min)</option>)}
                          </select>
                        </div>
                        <div className="col-span-4">
                          <input type="number" min="1" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" placeholder="Sessions/month" value={row.session_count} onChange={e => setSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, session_count: e.target.value } : r))} />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {sessionRows.length > 1 && <button onClick={() => setSessionRows(rows => rows.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-300 text-xs">✕</button>}
                        </div>
                      </div>
                    ))}
                    <button onClick={() => setSessionRows(rows => [...rows, { event_type_id: '', allocation_method: 'per_month', session_count: '1' }])} className="text-xs text-primary-400 hover:text-primary-300 transition-colors">+ Add session type</button>
                  </div>
                </div>
              </>)}

              {/* ── session_bundle form ── */}
              {selectedPackageType === 'session_bundle' && (<>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Package Name *</label>
                    <input className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="e.g. 10-Session Bundle" value={bundleForm.name} onChange={e => setBundleForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Description</label>
                    <textarea rows={2} className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600 resize-none" placeholder="What's included…" value={bundleForm.description} onChange={e => setBundleForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                </div>
                {/* Session Types */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Session Types</p>
                  <div className="space-y-2">
                    {sessionRows.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-7">
                          <select className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={row.event_type_id} onChange={e => setSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, event_type_id: e.target.value } : r))}>
                            <option value="">Select session type…</option>
                            {eventTypes.map(et => <option key={et.id} value={et.id}>{et.name} ({et.duration_minutes} min)</option>)}
                          </select>
                        </div>
                        <div className="col-span-4">
                          <input type="number" min="1" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" placeholder="Count" value={row.session_count} onChange={e => setSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, session_count: e.target.value } : r))} />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {sessionRows.length > 1 && <button onClick={() => setSessionRows(rows => rows.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-300 text-xs">✕</button>}
                        </div>
                      </div>
                    ))}
                    <button onClick={() => setSessionRows(rows => [...rows, { event_type_id: '', allocation_method: 'total', session_count: '1' }])} className="text-xs text-primary-400 hover:text-primary-300 transition-colors">+ Add session type</button>
                  </div>
                </div>
                {/* Pricing */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Pricing</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Bundle Price ($) *</label>
                      <input type="number" min="0" step="0.01" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="1200.00" value={bundleForm.total_price_dollars} onChange={e => setBundleForm(f => ({ ...f, total_price_dollars: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Currency</label>
                      <select className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={bundleForm.currency} onChange={e => setBundleForm(f => ({ ...f, currency: e.target.value }))}>
                        {currencyOptions(bundleForm.currency).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Expires After (months)</label>
                      <input type="number" min="1" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="No expiry" value={bundleForm.expiration_months} onChange={e => setBundleForm(f => ({ ...f, expiration_months: e.target.value }))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Pay-in-Full Discount (%)</label>
                      <input type="number" min="0" max="100" step="0.1" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="e.g. 10" value={bundleForm.pay_in_full_discount_percent} onChange={e => setBundleForm(f => ({ ...f, pay_in_full_discount_percent: e.target.value }))} />
                    </div>
                  </div>
                </div>
              </>)}

              {/* ── fixed_duration form (default) ── */}
              {selectedPackageType !== 'ongoing_monthly' && selectedPackageType !== 'session_bundle' && (<>
                {/* Basic info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Package Name *</label>
                    <input
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                      placeholder="e.g. 3-Month EF Foundations"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Description</label>
                    <textarea
                      rows={2}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600 resize-none"
                      placeholder="What's included in this package…"
                      value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Duration */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Duration</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Length *</label>
                      <input
                        type="number" min="1"
                        className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                        value={form.duration_value}
                        onChange={e => setForm(f => ({ ...f, duration_value: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Unit *</label>
                      <select
                        className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                        value={form.duration_unit}
                        onChange={e => {
                          const unit = e.target.value;
                          const method = unit === 'months' ? 'per_month' : 'per_week';
                          setForm(f => ({ ...f, duration_unit: unit }));
                          setSessionRows(rows => rows.map(r => ({ ...r, allocation_method: method })));
                        }}
                      >
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Session types */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Session Types</p>
                  <div className="space-y-2">
                    {sessionRows.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">
                          <select
                            className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                            value={row.event_type_id}
                            onChange={e => setSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, event_type_id: e.target.value } : r))}
                          >
                            <option value="">Select session type…</option>
                            {eventTypes.map(et => (
                              <option key={et.id} value={et.id}>{et.name} ({et.duration_minutes} min)</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <div className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-400 cursor-not-allowed select-none">
                            {form.duration_unit === 'months' ? 'Per month' : 'Per week'}
                          </div>
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number" min="1"
                            className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                            placeholder="Count"
                            value={row.session_count}
                            onChange={e => setSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, session_count: e.target.value } : r))}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {sessionRows.length > 1 && (
                            <button
                              onClick={() => setSessionRows(rows => rows.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >✕</button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={() => setSessionRows(rows => [...rows, { event_type_id: '', allocation_method: 'total', session_count: '1' }])}
                      className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      + Add session type
                    </button>
                  </div>
                </div>

                {/* Pricing */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Pricing</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Total Package Price ({form.currency}) *</label>
                      <input
                        type="number" min="0" step="0.01"
                        className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                        placeholder="1500.00"
                        value={form.total_price_dollars}
                        onChange={e => setForm(f => ({ ...f, total_price_dollars: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Currency</label>
                      <select
                        className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                        value={form.currency}
                        onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                      >
                        {currencyOptions(form.currency).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Pay-in-Full Discount (%)</label>
                      <input
                        type="number" min="0" max="100" step="0.1"
                        className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                        placeholder="e.g. 10"
                        value={form.pay_in_full_discount_percent}
                        onChange={e => setForm(f => ({ ...f, pay_in_full_discount_percent: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </>)}

              {formError && <p className="text-xs text-red-400">{formError}</p>}
              <div className="flex gap-2">
                <button
                  onClick={savePackage}
                  disabled={formSaving}
                  className="px-4 py-1.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {formSaving ? 'Saving…' : 'Save Package'}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-1.5 border border-dark-border text-gray-300 hover:text-white text-sm rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {packages.length === 0 && !showForm && !showTypeSelector && (
            <div className="bg-dark-card border border-dark-border rounded-xl p-8 text-center">
              <p className="text-sm text-gray-400">No packages yet. Create your first package template.</p>
            </div>
          )}

          <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
            {packages.map(pkg => {
              const hasEnrollments = pkg.enrollment_count > 0;
              return (
              <div key={pkg.id} className={`bg-dark-card border border-dark-border rounded-xl p-4 flex flex-col min-h-[160px] ${editingPkgId === pkg.id ? 'col-span-full' : ''}`}>
                {editingPkgId === pkg.id ? (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-white">Edit Package</h4>

                    {/* Name + Description */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-400 mb-1">Name *</label>
                        <input className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-400 mb-1">Description</label>
                        <input className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="Optional" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                      </div>
                    </div>

                    {/* Duration — fixed_duration only */}
                    {pkg.package_type !== 'ongoing_monthly' && pkg.package_type !== 'session_bundle' && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Duration</label>
                        <div className="flex gap-2">
                          <input type="number" min="1" className="w-24 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={editForm.duration_value} onChange={e => setEditForm(f => ({ ...f, duration_value: e.target.value }))} />
                          <select className="w-36 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={editForm.duration_unit} onChange={e => setEditForm(f => ({ ...f, duration_unit: e.target.value }))}>
                            <option value="weeks">Weeks</option>
                            <option value="months">Months</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Session Types */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Session Types</p>
                      <div className="space-y-2">
                        {editSessionRows.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-7">
                              <select className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={row.event_type_id} onChange={e => setEditSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, event_type_id: e.target.value } : r))}>
                                <option value="">Select session type…</option>
                                {eventTypes.map(et => <option key={et.id} value={et.id}>{et.name} ({et.duration_minutes} min)</option>)}
                              </select>
                            </div>
                            <div className="col-span-4">
                              <input type="number" min="1" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                                placeholder={pkg.package_type === 'ongoing_monthly' ? 'Sessions/month' : 'Count'}
                                value={row.session_count}
                                onChange={e => setEditSessionRows(rows => rows.map((r, i) => i === idx ? { ...r, session_count: e.target.value } : r))}
                              />
                            </div>
                            <div className="col-span-1 flex justify-end">
                              {editSessionRows.length > 1 && <button type="button" onClick={() => setEditSessionRows(rows => rows.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-300 text-xs">✕</button>}
                            </div>
                          </div>
                        ))}
                        <button type="button"
                          onClick={() => setEditSessionRows(rows => [...rows, {
                            event_type_id: '',
                            allocation_method: pkg.package_type === 'ongoing_monthly' ? 'per_month' : 'total',
                            session_count: '1',
                          }])}
                          className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                        >+ Add session type</button>
                      </div>
                    </div>

                    {/* Pricing */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Pricing</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            {pkg.package_type === 'ongoing_monthly' ? `Monthly Price (${editForm.currency}) *` : pkg.package_type === 'session_bundle' ? `Bundle Price (${editForm.currency}) *` : `Total Package Price (${editForm.currency}) *`}
                          </label>
                          <input type="number" min="0" step="0.01" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={editForm.total_price_dollars} onChange={e => setEditForm(f => ({ ...f, total_price_dollars: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Currency</label>
                          <select className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={editForm.currency} onChange={e => setEditForm(f => ({ ...f, currency: e.target.value }))}>
                            {currencyOptions(editForm.currency).map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Payment Method</label>
                          <select className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600" value={editForm.payment_method} onChange={e => setEditForm(f => ({ ...f, payment_method: e.target.value }))}>
                            <option value="offline">Offline / manual</option>
                            <option value="stripe">Stripe</option>
                          </select>
                        </div>
                        {pkg.package_type === 'session_bundle' && (
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Expires After (months)</label>
                            <input type="number" min="1" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="No expiry" value={editForm.expiration_months} onChange={e => setEditForm(f => ({ ...f, expiration_months: e.target.value }))} />
                          </div>
                        )}
                        {pkg.package_type !== 'ongoing_monthly' && (
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Pay-in-Full Discount (%)</label>
                            <input type="number" min="0" max="100" step="0.1" className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600" placeholder="e.g. 10" value={editForm.pay_in_full_discount_percent} onChange={e => setEditForm(f => ({ ...f, pay_in_full_discount_percent: e.target.value }))} />
                          </div>
                        )}
                      </div>
                      {pkg.package_type === 'ongoing_monthly' && (
                        <label className="flex items-center gap-2 cursor-pointer mt-3">
                          <input type="checkbox" className="accent-primary-500" checked={editForm.rollover_unused} onChange={e => setEditForm(f => ({ ...f, rollover_unused: e.target.checked }))} />
                          <span className="text-xs text-gray-300">Roll over unused sessions month to month</span>
                        </label>
                      )}
                    </div>

                    {editError && <p className="text-xs text-red-400">{editError}</p>}
                    <div className="flex gap-2">
                      <button onClick={saveEdit} disabled={editSaving} className="px-4 py-1.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">{editSaving ? 'Saving…' : 'Save'}</button>
                      <button onClick={() => setEditingPkgId(null)} className="px-4 py-1.5 border border-dark-border text-gray-300 hover:text-white text-sm rounded-lg transition-colors">Cancel</button>
                    </div>
                  </div>
                ) : (
                <div className="flex flex-col h-full gap-3">
                  {/* Card top: name + status */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <p className="text-sm font-semibold text-white leading-snug">{pkg.name}</p>
                      {pkg.is_active
                        ? <span className="text-[10px] bg-green-900/40 text-green-400 px-1.5 py-0.5 rounded-full flex-shrink-0">Active</span>
                        : <span className="text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full flex-shrink-0">Inactive</span>
                      }
                    </div>
                    <span className="inline-block text-[10px] text-gray-500 bg-dark-hover border border-dark-border rounded-full px-2 py-0.5 mb-2">
                      {pkg.package_type === 'ongoing_monthly' ? 'Monthly' : pkg.package_type === 'session_bundle' ? 'Bundle' : 'Fixed Duration'}
                    </span>
                    <p className="text-sm font-medium text-primary-300">
                      {pkg.package_type === 'ongoing_monthly'
                        ? `${formatCurrency((pkg.config_values as Record<string, number>)?.monthly_price_cents ?? null, pkg.currency)}/mo`
                        : pkg.package_type === 'session_bundle'
                          ? `${(pkg.config_values as Record<string, number>)?.total_sessions ?? '?'} sessions · ${formatCurrency(pkg.total_price_cents, pkg.currency)}`
                          : `${pkg.duration_value} ${pkg.duration_unit} · ${formatCurrency(pkg.total_price_cents, pkg.currency)}`
                      }
                    </p>
                    {pkg.session_types?.length > 0 && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        {pkg.session_types.map(st =>
                          `${st.session_count}${allocationLabel(st.allocation_method)} × ${st.session_type_name}`
                        ).join(' + ')}
                      </p>
                    )}
                    {pkg.description && <p className="text-[11px] text-gray-500 mt-1 italic line-clamp-2">{pkg.description}</p>}
                  </div>
                  {/* External booking toggle */}
                  {pkg.is_active && (
                    <div className="flex items-center justify-between py-1.5 border-t border-dark-border mt-1">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleExternalBooking(pkg)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${pkg.allow_external_booking ? 'bg-blue-600' : 'bg-gray-600'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${pkg.allow_external_booking ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                        <span className="text-[11px] text-gray-400">External booking</span>
                      </div>
                      {pkg.allow_external_booking && (
                        <span className="text-[10px] bg-blue-900/40 text-blue-400 px-1.5 py-0.5 rounded-full">On</span>
                      )}
                    </div>
                  )}
                  {/* Card actions */}
                  <div className="flex flex-wrap gap-1.5 pt-2 border-t border-dark-border">
                    {hasEnrollments && (
                      <button
                        onClick={async () => {
                          const fetched = await loadEnrollmentsForPackage(pkg.id);
                          setEnrollmentsModal({ pkgId: pkg.id, pkgName: pkg.name, pkgType: pkg.package_type });
                          await loadPausesForPackage(pkg.id, fetched);
                        }}
                        className="text-[11px] text-violet-400 hover:text-violet-300 border border-dark-border px-2 py-1 rounded-lg transition-colors"
                      >
                        Enrollments
                      </button>
                    )}
                    {pkg.is_active && (
                      <button onClick={() => openEnroll(pkg)} className="text-[11px] text-primary-400 hover:text-primary-300 border border-dark-border px-2 py-1 rounded-lg transition-colors">
                        Enroll Client
                      </button>
                    )}
                    <button onClick={() => openEdit(pkg)} className="text-[11px] text-gray-400 hover:text-white border border-dark-border px-2 py-1 rounded-lg transition-colors">
                      Edit
                    </button>
                    {!hasEnrollments && (
                      <button onClick={() => deletePackage(pkg)} className="text-[11px] text-red-400 hover:text-red-300 border border-dark-border px-2 py-1 rounded-lg transition-colors">
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                )}

              </div>
              );
            })}
          </div>
        </div>}

      {/* ── Enrollments modal ──────────────────────────────────────────────────── */}
      {enrollmentsModal && (() => {
        const pkgEnrollments = enrollments.filter(e => e.package_id === enrollmentsModal.pkgId);
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={() => { setEnrollmentsModal(null); setExpandedEnrollments(new Set()); }}
          >
            <div
              className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[85vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-dark-border flex-shrink-0">
                <div>
                  <h2 className="text-sm font-semibold text-white">Enrollments</h2>
                  <p className="text-xs text-gray-400 mt-0.5">{enrollmentsModal.pkgName}</p>
                </div>
                <button onClick={() => { setEnrollmentsModal(null); setExpandedEnrollments(new Set()); }} className="text-gray-500 hover:text-white text-lg leading-none">✕</button>
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-4 space-y-3">
                {pkgEnrollments.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-6">No enrollments yet.</p>
                )}
                {pkgEnrollments.map(e => {
                  const sessExpanded = expandedEnrollments.has(e.id);
                  const sessions = enrollmentSessions[e.id] ?? [];
                  const sessLoading = enrollmentSessionsLoading.has(e.id);
                  const pauses = enrollmentPauses[e.id] ?? [];
                  const now = new Date();
                  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                  const currentMonthPaused = pauses.some(p => p.pause_month === currentMonthKey);
                  return (
                    <div key={e.id} className="bg-dark-canvas border border-dark-border rounded-xl">
                      {/* Row */}
                      <div className="flex items-start justify-between gap-3 p-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-white">{e.client_name}</p>
                            <StatusBadge status={e.status} />
                          </div>
                          {e.start_date && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Started {new Date(e.start_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                          )}
                          {/* Session type usage bars */}
                          {e.session_usage?.map((u, i) => (
                            <div key={i} className="mt-1.5 flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-28 truncate">{u.session_type_name}</span>
                              <div className="flex-1 bg-dark-card rounded-full h-1.5">
                                <div
                                  className="bg-primary-500 h-1.5 rounded-full transition-all"
                                  style={{ width: `${Math.min(100, (u.sessions_used / u.sessions_total) * 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 flex-shrink-0">{u.sessions_used}/{u.sessions_total}</span>
                            </div>
                          ))}
                          {/* Pause badges */}
                          {pauses.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {pauses.map(p => (
                                <span key={p.pause_month} className="inline-flex items-center gap-1 text-xs bg-yellow-900/40 text-yellow-400 border border-yellow-800/50 px-2 py-0.5 rounded-full">
                                  Paused {new Date(p.pause_month + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                                  <button
                                    onClick={() => unpauseEnrollment(e.id, p.pause_month)}
                                    className="hover:text-yellow-200 ml-0.5 leading-none"
                                    title="Remove pause"
                                  >✕</button>
                                </span>
                              ))}
                            </div>
                          )}
                          {/* Collapsible sessions toggle */}
                          <button
                            onClick={() => toggleEnrollmentSessions(e.id)}
                            className="mt-2 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                          >
                            <svg className={`w-3 h-3 transition-transform ${sessExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                            {sessExpanded ? 'Hide sessions' : 'View sessions'}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => openViewEdit(e)}
                            className="text-xs text-gray-300 hover:text-white border border-dark-border px-2.5 py-1 rounded-lg transition-colors"
                          >
                            View / Edit
                          </button>
                          {enrollmentsModal?.pkgType === 'ongoing_monthly' && e.status === 'active' && !currentMonthPaused && !e.cancel_effective_month && (
                            <button
                              onClick={() => pauseEnrollment(e.id)}
                              className="text-xs text-yellow-400 hover:text-yellow-300 border border-dark-border px-2.5 py-1 rounded-lg transition-colors"
                            >
                              Pause Month
                            </button>
                          )}
                          {(e.status === 'active' || e.status === 'paused') && (
                            enrollmentsModal?.pkgType === 'ongoing_monthly' ? (
                              e.cancel_effective_month ? (
                                <span className="text-xs text-amber-400 border border-dark-border px-2.5 py-1 rounded-lg">
                                  Cancels {new Date(e.cancel_effective_month + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                                </span>
                              ) : (
                                <button
                                  onClick={() => { setCancelEnrollTarget({ id: e.id, client_name: e.client_name }); setCancelEffectiveMonth(''); }}
                                  className="text-xs text-red-400 hover:text-red-300 border border-dark-border px-2.5 py-1 rounded-lg transition-colors"
                                >
                                  Cancel Enrollment
                                </button>
                              )
                            ) : (
                              <button
                                onClick={() => updateEnrollmentStatus(e.id, 'cancelled')}
                                className="text-xs text-red-400 hover:text-red-300 border border-dark-border px-2.5 py-1 rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                            )
                          )}
                        </div>
                      </div>

                      {/* Collapsible sessions list */}
                      {sessExpanded && (
                        <div className="border-t border-dark-border px-3 pb-3 pt-2">
                          {sessLoading && <p className="text-xs text-gray-500">Loading…</p>}
                          {!sessLoading && sessions.length === 0 && (
                            <p className="text-xs text-gray-500 italic">No sessions linked to this enrollment yet.</p>
                          )}
                          {!sessLoading && sessions.map((s: any) => {
                            const dt = new Date(s.scheduled_at);
                            const dateStr = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                            const timeStr = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
                            const statusColors: Record<string, string> = {
                              scheduled: 'text-gray-400', completed: 'text-green-400',
                              no_show: 'text-amber-400', cancelled: 'text-red-400', in_progress: 'text-blue-400',
                            };
                            return (
                              <div key={s.id} className="flex items-center justify-between py-1.5 border-b border-dark-border last:border-0">
                                <div>
                                  <p className="text-xs text-gray-200">{s.meeting_name || 'Coaching Session'}</p>
                                  <p className="text-xs text-gray-500">{dateStr} · {timeStr} · {s.duration_minutes} min</p>
                                </div>
                                <span className={`text-xs capitalize ${statusColors[s.status] ?? 'text-gray-400'}`}>
                                  {s.status === 'no_show' ? 'No-Show' : s.status}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Cancel Enrollment modal ─────────────────────────────────────────────── */}
      {cancelEnrollTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
          onClick={() => setCancelEnrollTarget(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm shadow-2xl p-6"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-white mb-1">Cancel Enrollment</h3>
            <p className="text-xs text-gray-400 mb-4">
              {cancelEnrollTarget.client_name} — pick the month cancellation takes effect. The client keeps access through the end of the prior month.
            </p>
            <label className="block text-xs text-gray-400 mb-1">Effective month</label>
            <select
              value={cancelEffectiveMonth}
              onChange={e => setCancelEffectiveMonth(e.target.value)}
              className="w-full bg-dark-canvas border border-dark-border text-white text-sm rounded-lg px-3 py-2 mb-5"
            >
              <option value="">Select a month…</option>
              {futureMonthOptions().map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setCancelEnrollTarget(null)}
                className="text-xs text-gray-400 hover:text-white border border-dark-border px-3 py-1.5 rounded-lg transition-colors"
              >
                Keep Active
              </button>
              <button
                onClick={confirmCancelEnrollment}
                disabled={!cancelEffectiveMonth}
                className="text-xs bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg transition-colors"
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View / Edit enrollment modal ────────────────────────────────────────── */}
      {viewEditEnrollment && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setViewEditEnrollment(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-dark-border flex-shrink-0">
              <div>
                <h2 className="text-sm font-semibold text-white">{viewEditEnrollment.client_name}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{viewEditEnrollment.package_name}</p>
              </div>
              <button onClick={() => setViewEditEnrollment(null)} className="text-gray-500 hover:text-white text-lg leading-none">✕</button>
            </div>

            <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">

              {/* Status + Enrolled date */}
              <div className="flex items-center gap-3 flex-wrap">
                <StatusBadge status={viewEditEnrollment.status} />
                <span className="text-xs text-gray-500">
                  Enrolled {formatDateDisplay(viewEditEnrollment.enrolled_at?.split('T')[0] ?? null)}
                </span>
              </div>

              {/* Dates — start (conditionally editable) + end (always editable, push-out only) */}
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Dates</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    {(viewEditEnrollment.package_type === 'fixed_duration' || viewEditEnrollment.sessions_used > 0) ? (
                      <div className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed">
                        {formatDateDisplay(viewEditEnrollment.start_date)}
                      </div>
                    ) : (
                      <input
                        type="date"
                        style={{ colorScheme: 'dark' }}
                        className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                        value={viewEditStartDate}
                        onChange={e => setViewEditStartDate(e.target.value)}
                      />
                    )}
                    {viewEditEnrollment.package_type !== 'fixed_duration' && viewEditEnrollment.sessions_used > 0 && (
                      <p className="text-xs text-gray-600 mt-1 italic">Locked — sessions booked</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                    <input
                      type="date"
                      style={{ colorScheme: 'dark' }}
                      min={viewEditEnrollment.end_date ?? viewEditEnrollment.start_date ?? undefined}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                      value={viewEditEndDate}
                      onChange={e => setViewEditEndDate(e.target.value)}
                    />
                    <p className="text-xs text-gray-600 mt-1 italic">Can only extend forward</p>
                  </div>
                </div>
              </div>

              {/* Session usage */}
              {viewEditEnrollment.session_usage?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Session Usage</p>
                  {viewEditEnrollment.session_usage.map((u, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-32 truncate">{u.session_type_name}</span>
                      <div className="flex-1 bg-dark-canvas rounded-full h-1.5">
                        <div
                          className="bg-primary-500 h-1.5 rounded-full"
                          style={{ width: `${Math.min(100, (u.sessions_used / u.sessions_total) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{u.sessions_used}/{u.sessions_total}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Payment info */}
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Payment</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Method</label>
                    {viewEditEnrollment.package_type === 'fixed_duration' ? (
                      <select
                        className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                        value={viewEditPaymentMethod}
                        onChange={e => setViewEditPaymentMethod(e.target.value)}
                      >
                        <option value="offline">Offline / manual</option>
                        <option value="stripe">Stripe (Auto-Pay)</option>
                      </select>
                    ) : (
                      <div className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed capitalize">
                        {viewEditEnrollment.payment_method?.replace('_', ' ') ?? '—'}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Total</label>
                    <div className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed">
                      {formatCurrency(viewEditEnrollment.effective_total_cents ?? null, viewEditEnrollment.effective_currency ?? null)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Auto-Pay</label>
                    <div className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm cursor-not-allowed">
                      {(viewEditPaymentMethod || viewEditEnrollment.payment_method) === 'stripe'
                        ? <span className="text-green-400">Enabled</span>
                        : <span className="text-gray-500">Off (collect manually)</span>
                      }
                    </div>
                  </div>
                  {viewEditEnrollment.effective_first_payment_timing && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">First Payment</label>
                      <div className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed">
                        {{
                          on_start_date: 'On start date',
                          at_purchase: 'At enrollment',
                          custom_date: 'Custom date',
                        }[viewEditEnrollment.effective_first_payment_timing] ?? viewEditEnrollment.effective_first_payment_timing}
                      </div>
                    </div>
                  )}
                  {viewEditEnrollment.effective_allow_installments && (
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Installment Schedule</label>
                      <div className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-500 cursor-not-allowed">
                        {viewEditEnrollment.effective_installment_count ?? '?'} payments &middot; {viewEditEnrollment.effective_installment_frequency ?? ''}
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment rows */}
                {viewEditEnrollment.payments?.length > 0 && (
                  <div className="mt-3 divide-y divide-dark-border border border-dark-border rounded-lg overflow-hidden">
                    {viewEditEnrollment.payments.map(p => {
                      const statusColor: Record<string, string> = {
                        pending: 'text-yellow-400', succeeded: 'text-green-400',
                        manual: 'text-green-400', paid: 'text-green-400',
                        failed: 'text-red-400', refunded: 'text-gray-400',
                      };
                      const statusLabel: Record<string, string> = {
                        succeeded: 'Paid (Stripe)', manual: 'Paid', paid: 'Paid',
                        pending: 'Pending', failed: 'Failed', refunded: 'Refunded',
                      };
                      const isOffline = (viewEditPaymentMethod || viewEditEnrollment.payment_method) === 'offline';
                      const canMarkPaid = p.status === 'pending' || p.status === 'failed';
                      const busyMark = markPaidLoading === p.id;
                      const busyInv = invoiceLoading === p.id;
                      const paymentLabel = p.payment_type === 'installment'
                        ? `Payment #${p.installment_number}`
                        : p.payment_type === 'pay_in_full' ? 'Pay in full'
                        : p.payment_type === 'setup_fee' ? 'Setup fee'
                        : p.payment_type;
                      return (
                        <div key={p.id} className="px-3 py-2.5">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-xs text-gray-400 font-medium">{paymentLabel}</span>
                              {p.due_date && (
                                <span className="text-xs text-gray-600 ml-1">· due {formatDateDisplay(p.due_date)}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                              <span className="text-xs text-gray-400">{formatCurrency(p.amount_cents, p.currency)}</span>
                              <span className={`text-xs ${statusColor[p.status] ?? 'text-gray-400'}`}>
                                {statusLabel[p.status] ?? p.status}
                              </span>
                            </div>
                          </div>
                          {/* Failure reason */}
                          {p.status === 'failed' && p.failure_reason && (
                            <p className="text-[10px] text-red-400 mt-1 italic">{p.failure_reason}</p>
                          )}
                          {/* Action buttons */}
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {p.invoice_token && (
                              <a
                                href={`/invoice/${p.invoice_token}`}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2 py-0.5 text-[10px] font-medium bg-primary-900/40 text-primary-400 border border-primary-800/60 rounded hover:bg-primary-800/50 transition-colors"
                              >
                                View Invoice ↗
                              </a>
                            )}
                            {p.invoice_token && (
                              <button
                                disabled={busyInv}
                                onClick={() => handleCreateInvoice(viewEditEnrollment.id, p.id, true)}
                                className="px-2 py-0.5 text-[10px] font-medium bg-gray-700 text-gray-300 border border-gray-600 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                              >
                                {busyInv ? '…' : 'Resend Invoice'}
                              </button>
                            )}
                            {isOffline && !p.invoice_token && canMarkPaid && (
                              <button
                                disabled={busyInv}
                                onClick={() => handleCreateInvoice(viewEditEnrollment.id, p.id, false)}
                                className="px-2 py-0.5 text-[10px] font-medium bg-blue-900/40 text-blue-400 border border-blue-800/60 rounded hover:bg-blue-800/50 transition-colors disabled:opacity-50 whitespace-nowrap"
                              >
                                {busyInv ? '…' : 'Send Invoice'}
                              </button>
                            )}
                            {canMarkPaid && (
                              <button
                                onClick={() => handleChargeStripe(viewEditEnrollment.id, p.id, viewEditEnrollment.package_name ?? '', paymentLabel)}
                                className="px-2 py-0.5 text-[10px] font-medium bg-indigo-900/40 text-indigo-300 border border-indigo-800/60 rounded hover:bg-indigo-800/50 transition-colors whitespace-nowrap"
                              >
                                Charge via Stripe
                              </button>
                            )}
                            {canMarkPaid && (
                              <button
                                disabled={busyMark}
                                onClick={() => handleMarkPaid(viewEditEnrollment.id, p.id)}
                                className="px-2 py-0.5 text-[10px] font-medium bg-green-900/40 text-green-400 border border-green-800/60 rounded hover:bg-green-800/50 transition-colors disabled:opacity-50 whitespace-nowrap"
                              >
                                {busyMark ? '…' : 'Mark Paid'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">Notes</label>
                {viewEditEnrollment.package_type === 'fixed_duration' ? (
                  <div className="w-full px-3 py-2 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-500 cursor-not-allowed min-h-[4rem]">
                    {viewEditEnrollment.notes || <span className="italic">No notes</span>}
                  </div>
                ) : (
                  <textarea
                    rows={3}
                    value={viewEditNotes}
                    onChange={e => setViewEditNotes(e.target.value)}
                    placeholder="Internal notes about this enrollment…"
                    className="w-full px-3 py-2 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 placeholder-dark-muted resize-none focus:outline-none focus:border-primary-500"
                  />
                )}
              </div>

              {viewEditError && <p className="text-xs text-red-400">{viewEditError}</p>}
            </div>

            <div className="px-5 pb-5 pt-3 border-t border-dark-border flex gap-2 justify-end flex-shrink-0">
              <button
                onClick={() => setViewEditEnrollment(null)}
                className="px-4 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
              >
                Close
              </button>
              <button
                disabled={viewEditSaving}
                onClick={saveViewEdit}
                className="px-4 py-1.5 text-sm bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
              >
                {viewEditSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Enroll modal ─────────────────────────────────────────────────────── */}
      {enrollModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={() => setEnrollModal(null)}
        >
          <div
            className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-dark-border flex-shrink-0">
              <h2 className="text-sm font-semibold text-white">Enroll Client — {enrollModal.pkgName}</h2>
              <button onClick={() => setEnrollModal(null)} className="text-gray-500 hover:text-white text-lg leading-none">✕</button>
            </div>

            {/* Scrollable body */}
            <div className="px-6 py-5 space-y-5 overflow-y-auto">

              {/* Client + Start Date + End Date */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Client *</label>
                  <select
                    className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                    value={enrollModal.clientId}
                    onChange={e => updateEnroll('clientId', e.target.value)}
                  >
                    <option value="">Select client…</option>
                    {clients.map(c => (
                      <option key={c.relationship_id} value={c.relationship_id}>{c.client_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Start Date *</label>
                  {enrollModal.pkgType !== 'session_bundle' ? (
                    <select
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                      value={enrollModal.startDate}
                      onChange={e => {
                        const iso = e.target.value;
                        updateEnroll('startDate', iso);
                        if (enrollModal.pkgType === 'ongoing_monthly') {
                          const now = new Date();
                          const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                          if (iso === thisMonth) updateEnroll('firstPaymentTiming', 'at_purchase');
                          else if (enrollModal.firstPaymentTiming === 'at_purchase') updateEnroll('firstPaymentTiming', 'on_start_date');
                        }
                      }}
                    >
                      <option value="">Select month…</option>
                      {(() => {
                        const opts = [];
                        const now = new Date();
                        for (let i = 0; i < 7; i++) {
                          const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
                          const iso = d.toISOString().split('T')[0];
                          const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                          opts.push(<option key={iso} value={iso}>{label}</option>);
                        }
                        return opts;
                      })()}
                    </select>
                  ) : (
                    <input
                      type="date"
                      style={{ colorScheme: 'dark' }}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                      value={enrollModal.startDate}
                      onChange={e => updateEnroll('startDate', e.target.value)}
                    />
                  )}
                </div>
                {enrollModal.pkgType === 'fixed_duration' && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">End Date</label>
                    <div className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-400 cursor-not-allowed select-none">
                      {computeEndDate(enrollModal.startDate, enrollModal.pkgDurationValue, enrollModal.pkgDurationUnit)
                        ? formatDateDisplay(computeEndDate(enrollModal.startDate, enrollModal.pkgDurationValue, enrollModal.pkgDurationUnit))
                        : <span className="text-gray-600 italic">Set start date</span>
                      }
                    </div>
                  </div>
                )}
                {(enrollModal.pkgType === 'ongoing_monthly' || enrollModal.pkgType === 'session_bundle') && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">End Date <span className="text-gray-600 font-normal">(optional)</span></label>
                    <input
                      type="date"
                      style={{ colorScheme: 'dark' }}
                      className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                      value={enrollModal.endDate}
                      onChange={e => updateEnroll('endDate', e.target.value)}
                    />
                    <p className="text-xs text-gray-600 mt-1 italic">
                      {enrollModal.pkgType === 'ongoing_monthly'
                        ? 'Leave blank for ongoing with no fixed end'
                        : enrollModal.pkgExpirationMonths
                          ? `Default: ${enrollModal.pkgExpirationMonths} months from start — override here`
                          : 'Leave blank for no expiry'}
                    </p>
                  </div>
                )}
              </div>

              {/* Client card status */}
              {enrollModal.paymentMethod === 'stripe' && stripeConnected && enrollModal.clientId && clientCardInfo !== null && (
                clientCardInfo.has_card ? (
                  <div className="flex items-center gap-2 rounded-lg bg-green-900/20 border border-green-800/40 px-3 py-2">
                    <span className="text-xs text-green-400">
                      Card on file: {clientCardInfo.card_brand} ending {clientCardInfo.card_last4}
                    </span>
                  </div>
                ) : (
                  <div className="rounded-lg bg-yellow-900/20 border border-yellow-700/40 px-3 py-2 space-y-1">
                    <p className="text-xs text-yellow-300 font-medium">No card on file for this client</p>
                    <p className="text-xs text-yellow-500">Autopay will fail until the client pays their first invoice and saves a card. You can still enroll and switch to Offline if needed.</p>
                    <button
                      type="button"
                      onClick={() => updateEnroll('paymentMethod', 'offline')}
                      className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
                    >
                      Switch to Offline instead
                    </button>
                  </div>
                )
              )}

              {/* Stripe not connected warning */}
              {enrollModal.paymentMethod === 'stripe' && !stripeConnected && (
                <div className="border border-yellow-600/50 bg-yellow-900/20 rounded-lg p-3 space-y-2">
                  <p className="text-sm text-yellow-300 font-medium">Stripe is not connected</p>
                  <p className="text-xs text-yellow-400">You need to connect Stripe before you can charge clients automatically.</p>
                  <div className="flex gap-2 flex-wrap">
                    {onGoToPaymentSettings && (
                      <button
                        onClick={onGoToPaymentSettings}
                        className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-500 text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        Connect Stripe
                      </button>
                    )}
                    <button
                      onClick={() => updateEnroll('paymentMethod', 'offline')}
                      className="px-3 py-1.5 border border-dark-border text-gray-300 hover:text-white text-xs rounded-lg transition-colors"
                    >
                      Switch to Offline
                    </button>
                    <button
                      onClick={() => setEnrollModal(null)}
                      className="px-3 py-1.5 text-gray-500 hover:text-gray-300 text-xs rounded-lg transition-colors"
                    >
                      Cancel enrollment
                    </button>
                  </div>
                </div>
              )}

              {/* Pricing */}
              <div className="border-t border-dark-border pt-4 space-y-3">
                <p className="text-xs font-semibold text-gray-300">Pricing</p>

                {enrollModal.pkgType === 'ongoing_monthly' ? (
                  /* ongoing_monthly — editable monthly charge + payment method */
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Monthly Charge</label>
                        <div className="flex gap-1.5">
                          <select
                            className="bg-dark-canvas border border-dark-border rounded-lg px-2 py-2 text-xs text-white focus:outline-none focus:border-primary-600 w-20"
                            value={enrollModal.currency}
                            onChange={e => updateEnroll('currency', e.target.value)}
                          >
                            {currencyOptions(enrollModal.currency).map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <input
                            type="number" min="0" step="0.01" placeholder="0.00"
                            className="flex-1 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                            value={enrollModal.customPriceDollars}
                            onChange={e => updateEnroll('customPriceDollars', e.target.value)}
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">Override the package default for this client</p>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Payment Method</label>
                        <select
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                          value={enrollModal.paymentMethod}
                          onChange={e => updateEnroll('paymentMethod', e.target.value)}
                        >
                          <option value="offline">Offline / manual</option>
                          <option value="stripe">Stripe (Auto-Pay)</option>
                        </select>
                      </div>
                    </div>

                    {/* First billing date */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">First Billing Date</label>
                      {(() => {
                        const now = new Date();
                        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                        const isCurrentMonth = enrollModal.startDate === thisMonth;
                        if (isCurrentMonth) {
                          return (
                            <div className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-400 cursor-not-allowed select-none">
                              Day of enrollment (today)
                            </div>
                          );
                        }
                        return (
                          <>
                            <select
                              className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                              value={enrollModal.firstPaymentTiming}
                              onChange={e => updateEnroll('firstPaymentTiming', e.target.value)}
                            >
                              <option value="on_start_date">1st of enrollment month (start date)</option>
                              <option value="at_purchase">Day of enrollment (today)</option>
                              <option value="custom_date">Custom date</option>
                            </select>
                            {enrollModal.firstPaymentTiming === 'custom_date' && (
                              <input
                                type="date"
                                className="mt-2 w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                                value={enrollModal.firstPaymentDate}
                                onChange={e => updateEnroll('firstPaymentDate', e.target.value)}
                              />
                            )}
                          </>
                        );
                      })()}
                      <p className="text-xs text-gray-600 mt-1">Recurring charges are billed on the 1st of each month automatically.</p>
                    </div>
                  </>
                ) : (
                  /* fixed_duration / session_bundle — editable price + installments */
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">
                          {enrollModal.pkgType === 'session_bundle' ? 'Bundle Price' : 'Total Price'}
                        </label>
                        <div className="flex gap-1.5">
                          <select
                            className="bg-dark-canvas border border-dark-border rounded-lg px-2 py-2 text-xs text-white focus:outline-none focus:border-primary-600 w-20"
                            value={enrollModal.currency}
                            onChange={e => updateEnroll('currency', e.target.value)}
                          >
                            {currencyOptions(enrollModal.currency).map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          <input
                            type="number" min="0" step="0.01" placeholder="0.00"
                            className="flex-1 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                            value={enrollModal.customPriceDollars}
                            onChange={e => updateEnroll('customPriceDollars', e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Payment Method</label>
                        <select
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                          value={enrollModal.paymentMethod}
                          onChange={e => updateEnroll('paymentMethod', e.target.value)}
                        >
                          <option value="offline">Offline / manual</option>
                          <option value="stripe">Stripe (Auto-Pay)</option>
                        </select>
                      </div>
                    </div>

                    {/* Pay-in-full discount info */}
                    {!enrollModal.allowInstallments && (enrollModal.pkgDiscountPercent ?? 0) > 0 && enrollModal.customPriceDollars !== '' && (() => {
                      const total = parseFloat(enrollModal.customPriceDollars);
                      if (isNaN(total) || total <= 0) return null;
                      const discounted = total * (1 - enrollModal.pkgDiscountPercent! / 100);
                      const savings = total - discounted;
                      return (
                        <div className="rounded-lg bg-green-900/20 border border-green-800/40 px-3 py-2">
                          <p className="text-xs text-green-400 font-medium">
                            Pay in full: {enrollModal.currency} {discounted.toFixed(2)}
                            <span className="text-green-600 font-normal ml-1">({enrollModal.pkgDiscountPercent}% off — save {enrollModal.currency} {savings.toFixed(2)})</span>
                          </p>
                        </div>
                      );
                    })()}

                    {/* Session bundle full-payment date */}
                    {enrollModal.pkgType === 'session_bundle' && !enrollModal.allowInstallments && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Payment due date</label>
                        <div className="flex gap-1.5 flex-wrap">
                          {([
                            { val: 'at_purchase',   label: 'Enrollment date (today)' },
                            { val: 'on_start_date', label: 'Start date' },
                            { val: 'custom_date',   label: 'Custom date' },
                          ] as { val: string; label: string }[]).map(opt => (
                            <button
                              key={opt.val}
                              type="button"
                              onClick={() => {
                                if (opt.val !== 'custom_date') updateEnroll('firstPaymentDate', '');
                                updateEnroll('firstPaymentTiming', opt.val);
                              }}
                              className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                                enrollModal.firstPaymentTiming === opt.val
                                  ? 'bg-primary-600 border-primary-600 text-white'
                                  : 'border-dark-border text-gray-400 hover:text-white'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        {enrollModal.firstPaymentTiming === 'custom_date' && (
                          <input
                            type="date"
                            style={{ colorScheme: 'dark' }}
                            className={`mt-2 w-48 bg-dark-canvas border rounded-lg px-3 py-2 text-sm text-white focus:outline-none ${
                              enrollModal.firstPaymentDate && enrollModal.firstPaymentDate < new Date().toISOString().split('T')[0]
                                ? 'border-red-500 focus:border-red-400'
                                : 'border-dark-border focus:border-primary-600'
                            }`}
                            value={enrollModal.firstPaymentDate}
                            onChange={e => updateEnroll('firstPaymentDate', e.target.value)}
                          />
                        )}
                      </div>
                    )}

                    {/* Installments toggle */}
                    <label className="flex items-center gap-2 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        className="accent-primary-500"
                        checked={enrollModal.allowInstallments}
                        onChange={e => {
                          const checked = e.target.checked;
                          updateEnroll('allowInstallments', checked);
                          if (enrollModal.pkgType === 'session_bundle') {
                            if (checked) {
                              const total = parseFloat(enrollModal.customPriceDollars || '0');
                              const half = total > 0 ? (total / 2).toFixed(2) : '';
                              setEditableSchedule([
                                { payment_number: 1, due_date: '', amount_dollars: half, currency: enrollModal.currency },
                                { payment_number: 2, due_date: '', amount_dollars: half, currency: enrollModal.currency },
                              ]);
                            } else {
                              setEditableSchedule([]);
                            }
                          }
                        }}
                      />
                      <span className="text-xs text-gray-300">Split into installments</span>
                    </label>

                    {enrollModal.allowInstallments && enrollModal.pkgType === 'session_bundle' && (() => {
                      const target = parseFloat(enrollModal.customPriceDollars || '0');
                      const schedTotal = editableSchedule.reduce((s, r) => s + parseFloat(r.amount_dollars || '0'), 0);
                      const remaining = Math.max(0, target - schedTotal);
                      const balanced = Math.abs(schedTotal - target) < 0.005;
                      return (
                        <div className="space-y-2 pl-1">
                          <div className="divide-y divide-dark-border border border-dark-border rounded-lg overflow-hidden">
                            {editableSchedule.map((row, i) => (
                              <div key={row.payment_number} className="flex items-center gap-2 px-3 py-2">
                                <span className="text-xs text-gray-500 w-5 shrink-0">#{row.payment_number}</span>
                                <input
                                  type="date"
                                  style={{ colorScheme: 'dark' }}
                                  className={`bg-dark-canvas border rounded px-2 py-1 text-xs text-white focus:outline-none flex-1 ${
                                    row.due_date && row.due_date < new Date().toISOString().split('T')[0]
                                      ? 'border-red-500 focus:border-red-400'
                                      : 'border-dark-border focus:border-primary-600'
                                  }`}
                                  value={row.due_date}
                                  onChange={e => updateScheduleRow(i, 'due_date', e.target.value)}
                                />
                                <input
                                  type="number" min="0" step="0.01"
                                  className="bg-dark-canvas border border-dark-border rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-primary-600 w-24 text-right shrink-0"
                                  value={row.amount_dollars}
                                  onChange={e => updateScheduleRow(i, 'amount_dollars', e.target.value)}
                                />
                                <span className="text-xs text-gray-500 shrink-0">{enrollModal.currency}</span>
                                {editableSchedule.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => setEditableSchedule(rows => rows.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, payment_number: idx + 1 })))}
                                    className="text-red-400 hover:text-red-300 text-xs shrink-0"
                                  >✕</button>
                                )}
                              </div>
                            ))}
                            <div className={`flex items-center justify-between px-3 py-2 text-xs font-medium ${balanced ? 'text-green-400' : 'text-gray-400'}`}>
                              <span>Total</span>
                              <span>
                                {balanced
                                  ? `✓ ${schedTotal.toFixed(2)} ${enrollModal.currency}`
                                  : `${schedTotal.toFixed(2)} / ${target.toFixed(2)} ${enrollModal.currency} — ${remaining.toFixed(2)} remaining`
                                }
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditableSchedule(rows => [...rows, {
                              payment_number: rows.length + 1,
                              due_date: '',
                              amount_dollars: remaining > 0 ? remaining.toFixed(2) : '',
                              currency: enrollModal.currency,
                            }])}
                            className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                          >
                            + Add payment
                          </button>
                        </div>
                      );
                    })()}

                    {enrollModal.allowInstallments && enrollModal.pkgType !== 'session_bundle' && (
                      <div className="space-y-3 pl-1">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Frequency</label>
                            <select
                              className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                              value={enrollModal.installmentFrequency}
                              onChange={e => updateEnroll('installmentFrequency', e.target.value)}
                            >
                              <option value="monthly">Monthly</option>
                              <option value="weekly">Weekly</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              Number of payments
                              {enrollModal.installmentCount && (
                                <span className="text-gray-600 ml-1">(auto-calculated)</span>
                              )}
                            </label>
                            <input
                              type="number" min="2" step="1" placeholder="e.g. 3"
                              className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                              value={enrollModal.installmentCount}
                              onChange={e => updateEnroll('installmentCount', e.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-400 mb-1">First payment due</label>
                          <div className="flex gap-1.5">
                            {([
                              { val: 'on_start_date', label: 'On start date' },
                              { val: 'at_purchase',   label: 'At enrollment (today)' },
                              { val: 'custom_date',   label: 'Custom date' },
                            ] as { val: string; label: string }[]).map(opt => (
                              <button
                                key={opt.val}
                                type="button"
                                onClick={() => {
                                  if (opt.val !== 'custom_date') updateEnroll('firstPaymentDate', '');
                                  updateEnroll('firstPaymentTiming', opt.val);
                                }}
                                className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                                  enrollModal.firstPaymentTiming === opt.val
                                    ? 'bg-primary-600 border-primary-600 text-white'
                                    : 'border-dark-border text-gray-400 hover:text-white'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          {enrollModal.firstPaymentTiming === 'custom_date' && (
                            <input
                              type="date"
                              style={{ colorScheme: 'dark' }}
                              className={`mt-2 w-48 bg-dark-canvas border rounded-lg px-3 py-2 text-sm text-white focus:outline-none ${
                                enrollModal.firstPaymentDate && enrollModal.firstPaymentDate < new Date().toISOString().split('T')[0]
                                  ? 'border-red-500 focus:border-red-400'
                                  : 'border-dark-border focus:border-primary-600'
                              }`}
                              value={enrollModal.firstPaymentDate}
                              onChange={e => updateEnroll('firstPaymentDate', e.target.value)}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Payment schedule — editable, populated from backend (fixed_duration only) */}
              {enrollModal?.pkgType !== 'session_bundle' && (editableSchedule.length > 0 || scheduleLoading) && (() => {
                const schedTotal = editableSchedule.reduce((s, r) => s + parseFloat(r.amount_dollars || '0'), 0);
                const rawTarget = parseFloat(enrollModal.customPriceDollars || '0');
                const target = (!enrollModal.allowInstallments && enrollModal.pkgDiscountPercent && enrollModal.pkgDiscountPercent > 0)
                  ? Math.round(rawTarget * (1 - enrollModal.pkgDiscountPercent / 100) * 100) / 100
                  : rawTarget;
                const match = Math.abs(schedTotal - target) < 0.005;
                return (
                  <div className="rounded-lg border border-dark-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setScheduleOpen(o => !o)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-dark-canvas hover:bg-dark-border/40 transition-colors"
                    >
                      <span className="text-xs font-semibold text-gray-400">Payment schedule</span>
                      <span className="text-xs text-gray-500">{scheduleOpen ? '▲ hide' : '▼ show'}</span>
                    </button>
                    {scheduleOpen && (
                      <div>
                        {scheduleLoading ? (
                          <p className="px-3 py-2 text-xs text-gray-500">Calculating…</p>
                        ) : (
                          <>
                            <div className="divide-y divide-dark-border">
                              {editableSchedule.map((row, i) => (
                                <div key={row.payment_number} className="flex items-center gap-2 px-3 py-2">
                                  <span className="text-xs text-gray-500 w-6 shrink-0">#{row.payment_number}</span>
                                  <input
                                    type="date"
                                    style={{ colorScheme: 'dark' }}
                                    className={`bg-dark-canvas border rounded px-2 py-1 text-xs text-white focus:outline-none w-36 shrink-0 ${
                                      row.due_date && row.due_date < new Date().toISOString().split('T')[0]
                                        ? 'border-red-500 focus:border-red-400'
                                        : 'border-dark-border focus:border-primary-600'
                                    }`}
                                    value={row.due_date}
                                    onChange={e => updateScheduleRow(i, 'due_date', e.target.value)}
                                  />
                                  <input
                                    type="number" min="0" step="0.01"
                                    className="bg-dark-canvas border border-dark-border rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-primary-600 w-24 text-right shrink-0"
                                    value={row.amount_dollars}
                                    onChange={e => updateScheduleRow(i, 'amount_dollars', e.target.value)}
                                  />
                                  <span className="text-xs text-gray-500">{row.currency}</span>
                                </div>
                              ))}
                            </div>
                            <div className={`flex items-center justify-between px-3 py-2 border-t border-dark-border ${match ? 'text-green-400' : 'text-red-400'}`}>
                              <span className="text-xs font-medium">Total</span>
                              <span className="text-xs font-medium">
                                {match ? `✓ ${schedTotal.toFixed(2)} ${editableSchedule[0]?.currency ?? ''}` : `✗ ${schedTotal.toFixed(2)} / ${target.toFixed(2)} ${editableSchedule[0]?.currency ?? ''}`}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Notes */}
              <div className="border-t border-dark-border pt-4">
                <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
                <input
                  className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                  placeholder="Any enrollment notes…"
                  value={enrollModal.notes}
                  onChange={e => updateEnroll('notes', e.target.value)}
                />
              </div>

              {enrollError && <p className="text-xs text-red-400">{enrollError}</p>}
            </div>

            {/* Footer */}
            <div className="flex gap-2 px-6 pb-5 pt-3 border-t border-dark-border flex-shrink-0">
              {(() => {
                const schedMismatch = editableSchedule.length > 0 && (() => {
                  const schedTotal = editableSchedule.reduce((s, r) => s + parseFloat(r.amount_dollars || '0'), 0);
                  const rawTarget = parseFloat(enrollModal.customPriceDollars || '0');
                  const target = (!enrollModal.allowInstallments && enrollModal.pkgDiscountPercent && enrollModal.pkgDiscountPercent > 0)
                    ? Math.round(rawTarget * (1 - enrollModal.pkgDiscountPercent / 100) * 100) / 100
                    : rawTarget;
                  return Math.abs(schedTotal - target) >= 0.005;
                })();
                return (
                  <button
                    onClick={() => setShowEnrollConfirm(true)}
                    disabled={enrollSaving || !enrollModal.clientId || !enrollModal.startDate || !!schedMismatch}
                    title={schedMismatch ? 'Schedule total does not match package price' : undefined}
                    className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Confirm Enrollment
                  </button>
                );
              })()}
              <button
                onClick={() => setEnrollModal(null)}
                className="px-4 py-2 border border-dark-border text-gray-300 hover:text-white text-sm rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment confirmation dialog */}
      {showEnrollConfirm && enrollModal && (() => {
        const isStripe = enrollModal.paymentMethod === 'stripe';
        const clientName = clients.find(
          c => String(c.relationship_id) === String(enrollModal.clientId)
        )?.client_name ?? 'Unknown client';
        const startDisplay = enrollModal.startDate
          ? new Date(enrollModal.startDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
          : '—';
        const billingTiming = enrollModal.firstPaymentTiming;
        const firstBillDisplay = billingTiming === 'at_purchase'
          ? 'today'
          : billingTiming === 'custom_date' && enrollModal.firstPaymentDate
            ? new Date(enrollModal.firstPaymentDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
            : startDisplay;
        const amount = enrollModal.customPriceDollars ? parseFloat(enrollModal.customPriceDollars) : null;
        const amountDisplay = amount != null ? `${enrollModal.currency} ${amount.toFixed(2)}` : null;

        const actions: string[] = [
          `${clientName} is enrolled in ${enrollModal.pkgName}`,
          'Enrollment confirmation email sent to client and coach',
        ];
        if (isStripe) {
          actions.push('Invoice email sent to client');
          actions.push(
            billingTiming === 'at_purchase' || billingTiming === 'custom_date'
              ? "Client's card on file charged immediately"
              : `Card charged on ${firstBillDisplay}`
          );
        } else {
          actions.push(`Invoice email sent to client on ${firstBillDisplay}`);
        }

        return (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[70] px-4">
            <div className="bg-[#12121f] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">

              {/* Header */}
              <div className="px-6 pt-6 pb-5 border-b border-white/8">
                <h3 className="text-lg font-semibold text-white tracking-tight">Confirm Enrollment</h3>
                <p className="text-sm text-gray-500 mt-0.5">Review the details below before confirming.</p>
              </div>

              {/* Summary rows */}
              <div className="px-6 py-5 space-y-4 border-b border-white/8">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide w-28 shrink-0">Client</span>
                  <span className="text-sm text-white font-medium text-right">{clientName}</span>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide w-28 shrink-0">Package</span>
                  <span className="text-sm text-white text-right">{enrollModal.pkgName}</span>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide w-28 shrink-0">Start date</span>
                  <span className="text-sm text-white text-right">{startDisplay}</span>
                </div>
                {amountDisplay && (
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide w-28 shrink-0">First payment</span>
                    <span className="text-sm text-white text-right">
                      <span className="font-semibold">{amountDisplay}</span>
                      <span className="text-gray-400"> · due {firstBillDisplay}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Actions list */}
              <div className="px-6 py-5 border-b border-white/8">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">What happens now</p>
                <ul className="space-y-2.5">
                  {actions.map((action, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                      <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-primary-600/25 flex items-center justify-center text-primary-400 text-[10px] font-bold">✓</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Buttons */}
              <div className="px-6 py-4 flex gap-3">
                <button
                  onClick={submitEnroll}
                  disabled={enrollSaving}
                  className="flex-1 py-2.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
                >
                  {enrollSaving ? 'Enrolling…' : 'Confirm Enrollment'}
                </button>
                <button
                  onClick={() => setShowEnrollConfirm(false)}
                  className="px-5 py-2.5 border border-white/10 text-gray-400 hover:text-white text-sm rounded-xl transition-colors"
                >
                  Go back
                </button>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ── Mark Paid modal ── */}
      {markPaidPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMarkPaidPrompt(null)} />
          <div className="relative z-10 w-full max-w-sm bg-dark-card border border-dark-border rounded-xl shadow-xl p-6">
            <h3 className="text-sm font-semibold text-white mb-4">Mark as Paid</h3>
            <p className="text-sm text-gray-400 mb-4">How was this payment received?</p>
            <select
              value={markPaidMethod}
              onChange={e => setMarkPaidMethod(e.target.value)}
              className="w-full px-3 py-2 bg-dark-canvas border border-dark-border rounded-lg text-gray-200 text-sm focus:outline-none focus:border-primary-500 mb-5 [color-scheme:dark]"
            >
              {['Cash', 'Venmo', 'Zelle', 'PayPal', 'Check', 'Bank Transfer', 'Other'].map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button
                onClick={confirmMarkPaid}
                className="flex-1 py-2 bg-green-700 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setMarkPaidPrompt(null)}
                className="px-4 py-2 border border-dark-border text-gray-400 hover:text-gray-200 text-sm rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {chargeModal && (
        <PackagePaymentModal
          packagePaymentId={chargeModal.paymentId}
          packageName={chargeModal.packageName}
          label={chargeModal.paymentLabel}
          onClose={() => setChargeModal(null)}
          onSuccess={() => {
            handleChargeSuccess(chargeModal.enrollmentId, chargeModal.paymentId);
            setChargeModal(null);
          }}
        />
      )}
    </div>
  );
}
